import { useState, useEffect } from "react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ChevronLeft, ChevronRight, Activity, CheckCircle, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { scrollToFirstError } from "@/lib/utils";
import type { IntakeData } from "@/lib/types";
// import ThreeDHead from "./hair/ThreeDHead"; // Replaced with 2D version
import HairAreaSelector2D from "./hair/HairAreaSelector2D";
import { IntakeNavigation } from "./IntakeNavigation";

interface DetailedQuestionsStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const PRIMARY_GOAL_OPTIONS = [
  { value: "chest_pain", label: msg`Chest pain` },
  { value: "circulation_issues", label: msg`Circulation issues (legs, arms)` },
  { value: "varicose_veins", label: msg`Varicose veins` },
  { value: "high_blood_pressure", label: msg`High blood pressure` },
  { value: "stroke_prevention", label: msg`Stroke prevention` },
  { value: "other", label: msg`Other` },
];

const SYMPTOMS_STATUS_OPTIONS = [
  { value: "yes_daily", label: msg`Yes, daily` },
  { value: "yes_occasionally", label: msg`Yes, occasionally` },
  { value: "no_symptoms", label: msg`No symptoms, preventative evaluation` },
];

const SYMPTOM_TYPES_OPTIONS = [
  { value: "pain", label: msg`Pain` },
  { value: "swelling", label: msg`Swelling` },
  { value: "numbness", label: msg`Numbness` },
  { value: "shortness_of_breath", label: msg`Shortness of breath` },
  { value: "fatigue", label: msg`Fatigue` },
  { value: "other", label: msg`Other` },
];

const DIAGNOSIS_STATUS_OPTIONS = [
  { value: "known", label: msg`Yes (diagnosis known)` },
  { value: "unsure", label: msg`Yes (but unsure of details)` },
  { value: "seeking", label: msg`No, seeking evaluation` },
];

const URGENCY_OPTIONS = [
  { value: "immediately", label: msg`Immediately` },
  { value: "1_2_months", label: msg`Within 1–2 months` },
  { value: "flexible", label: msg`Flexible` },
];

const EXPECTATION_OPTIONS = [
  { value: "diagnostic", label: msg`Diagnostic testing only` },
  { value: "non_surgical", label: msg`Non-surgical treatment` },
  { value: "surgical", label: msg`Surgical intervention` },
  { value: "consultation", label: msg`Doctor recommendation` },
];

// Hair Transplant Constants
const HAIR_RELEVANT_FACTOR_OPTIONS = [
  {
    value: "pregnancy_breastfeeding",
    label: msg`Pregnancy or breastfeeding (now or in the past year)`,
  },
  {
    value: "postpartum",
    label: msg`Postpartum hair shedding (within the past 12 months)`,
  },
  {
    value: "menopause_hormonal",
    label: msg`Perimenopause or menopause-related hair changes`,
  },
  {
    value: "pcos_hormonal",
    label: msg`PCOS or other hormone-related hair loss`,
  },
  { value: "no_factors", label: msg`None of these apply` },
];

const HAIR_PROGRESSION_OPTIONS = [
  { value: "stable", label: msg`Stable` },
  { value: "slowly_worsening", label: msg`Slowly worsening` },
  { value: "rapidly_worsening", label: msg`Rapidly worsening` },
];

const HAIR_PREVIOUS_TREATMENTS = [
  { value: "none", label: msg`None` },
  {
    value: "medications",
    label: msg`Medications (e.g. minoxidil; spironolactone or finasteride only when prescribed)`,
  },
  { value: "prp", label: msg`PRP` },
  { value: "previous_transplant", label: msg`Previous transplant` },
];

const HAIR_OUTCOME_OPTIONS = [
  { value: "natural_hairline", label: msg`Natural-looking hairline` },
  { value: "part_mid_scalp", label: msg`Better density at the part / mid-scalp` },
  { value: "density", label: msg`Overall density` },
  { value: "coverage", label: msg`Coverage` },
  { value: "minimal_scarring", label: msg`Minimal scarring` },
];

const HAIR_TIMELINE_OPTIONS = [
  { value: "asap", label: msg`ASAP` },
  { value: "1_3_months", label: msg`1–3 months` },
  { value: "flexible", label: msg`Flexible` },
];

// Bariatric Constants
const BARIATRIC_GOAL_OPTIONS = [
  { value: "weight_loss", label: msg`Weight loss` },
  { value: "improve_conditions", label: msg`Improve obesity-related conditions` },
  { value: "improve_mobility", label: msg`Improve mobility` },
  { value: "other", label: msg`Other` },
];

const BARIATRIC_PRIOR_WEIGHT_LOSS_OPTIONS = [
  { value: "diet_exercise", label: msg`Diet & Exercise` },
  { value: "medications", label: msg`Weight loss medications` },
  { value: "previous_surgery", label: msg`Previous bariatric surgery` },
  { value: "none", label: msg`None` },
];

const BARIATRIC_PROCEDURE_OPTIONS = [
  { value: "gastric_sleeve", label: msg`Gastric Sleeve` },
  { value: "gastric_bypass", label: msg`Gastric Bypass` },
  { value: "gastric_balloon", label: msg`Gastric Balloon` },
  { value: "revision", label: msg`Revision Surgery` },
  { value: "undecided", label: msg`Undecided` },
];

const BARIATRIC_DIET_OPTIONS = [
  { value: "healthy", label: msg`Generally healthy` },
  { value: "emotional", label: msg`Emotional eating` },
  { value: "snacking", label: msg`Frequent snacking` },
  { value: "large_portions", label: msg`Large portions` },
  { value: "other", label: msg`Other` },
];

// Wellness & Healthcare Constants
const WELLNESS_HEALTHCARE_GOAL_OPTIONS = [
  { value: "preventive_care", label: msg`Preventive Care` },
  { value: "holistic_healing", label: msg`Holistic Healing` },
  { value: "anti_aging", label: msg`Anti-aging / Longevity` },
  { value: "health_optimization", label: msg`Health Optimization` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_HEALTHCARE_PRACTICES_OPTIONS = [
  { value: "nutritional_supplements", label: msg`Nutritional Supplements` },
  { value: "meditation_mindfulness", label: msg`Meditation / Mindfulness` },
  { value: "regular_exercise", label: msg`Regular Exercise` },
  { value: "alternative_medicine", label: msg`Alternative Medicine` },
  { value: "none", label: msg`None` },
];

const WELLNESS_HEALTHCARE_CONCERNS_OPTIONS = [
  { value: "sleep_issues", label: msg`Sleep Issues` },
  { value: "stress_anxiety", label: msg`Stress / Anxiety` },
  { value: "energy_levels", label: msg`Energy Levels` },
  { value: "nutrition", label: msg`Nutrition` },
  { value: "other", label: msg`Other` },
];

// Optical Constants
const OPTICAL_REASON_OPTIONS = [
  { value: "blurry_vision", label: msg`Blurry vision` },
  { value: "glasses_contacts", label: msg`Glasses or contacts` },
  { value: "lasik_evaluation", label: msg`LASIK evaluation` },
  { value: "cataracts", label: msg`Cataracts` },
  { value: "disease_monitoring", label: msg`Eye disease monitoring` },
  { value: "routine_exam", label: msg`Routine exam` },
  { value: "other", label: msg`Other` },
];

const OPTICAL_CORRECTION_OPTIONS = [
  { value: "glasses", label: msg`Glasses` },
  { value: "contacts", label: msg`Contact lenses` },
  { value: "both", label: msg`Both` },
  { value: "neither", label: msg`Neither` },
];

const OPTICAL_LATERALITY_OPTIONS = [
  { value: "left", label: msg`Left` },
  { value: "right", label: msg`Right` },
  { value: "both", label: msg`Both` },
];

const OPTICAL_SYMPTOMS_OPTIONS = [
  { value: "pain", label: msg`Eye pain` },
  { value: "headaches", label: msg`Headaches` },
  { value: "dry_eyes", label: msg`Dry eyes` },
  { value: "light_sensitivity", label: msg`Light sensitivity` },
  { value: "night_vision", label: msg`Night vision issues` },
  { value: "none", label: msg`None` },
];

const OPTICAL_PROCEDURE_INTEREST_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
  { value: "unsure", label: msg`Unsure / want evaluation` },
];

// Plastic Surgery Constants
const PLASTIC_AREA_OPTIONS = [
  { value: "face", label: msg`Face` },
  { value: "breast", label: msg`Breast` },
  { value: "body", label: msg`Body` },
  { value: "multiple", label: msg`Multiple areas` },
  { value: "other", label: msg`Other` },
];

const PLASTIC_PROCEDURE_TYPE_OPTIONS = [
  { value: "cosmetic", label: msg`Cosmetic enhancement` },
  { value: "reconstructive", label: msg`Reconstructive surgery` },
  { value: "revision", label: msg`Revision of prior surgery` },
  { value: "other", label: msg`Other` },
];

const PLASTIC_CONCERN_OPTIONS = [
  { value: "appearance", label: msg`Appearance improvement` },
  { value: "symmetry", label: msg`Symmetry` },
  { value: "function", label: msg`Function` },
  { value: "correction", label: msg`Correction of prior work` },
  { value: "other", label: msg`Other` },
];

const PLASTIC_PREVIOUS_SURGERY_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
];

const PLASTIC_OUTCOME_OPTIONS = [
  { value: "natural", label: msg`Natural look` },
  { value: "minimal_downtime", label: msg`Minimal downtime` },
  { value: "longevity", label: msg`Longevity of results` },
  { value: "cost", label: msg`Cost efficiency` },
  { value: "other", label: msg`Other` },
];

// Diagnostics Constants
const DIAGNOSTICS_PURPOSE_OPTIONS = [
  { value: "doctor_recommendation", label: msg`Doctor recommendation` },
  { value: "ongoing_condition", label: msg`Ongoing condition` },
  { value: "new_symptoms", label: msg`New symptoms` },
  { value: "preventative", label: msg`Preventative screening` },
  { value: "other", label: msg`Other` },
];

const DIAGNOSTICS_TYPE_OPTIONS = [
  { value: "mri", label: msg`MRI` },
  { value: "ct", label: msg`CT` },
  { value: "xray", label: msg`X-ray` },
  { value: "ultrasound", label: msg`Ultrasound` },
  { value: "pet", label: msg`PET` },
  { value: "unsure", label: msg`Not sure` },
];

const DIAGNOSTICS_AREA_OPTIONS = [
  { value: "head_brain", label: msg`Head/brain` },
  { value: "spine", label: msg`Spine` },
  { value: "chest", label: msg`Chest` },
  { value: "abdomen", label: msg`Abdomen` },
  { value: "pelvis", label: msg`Pelvis` },
  { value: "extremities", label: msg`Extremities` },
  { value: "other", label: msg`Other` },
];

const DIAGNOSTICS_SENSITIVITY_OPTIONS = [
  { value: "urgent", label: msg`Urgent` },
  { value: "within_weeks", label: msg`Within weeks` },
  { value: "flexible", label: msg`Flexible` },
];



const DIAGNOSTICS_LAB_REASON_OPTIONS = [
  { value: "routine_check", label: msg`Routine health check` },
  { value: "ongoing_symptoms", label: msg`Ongoing symptoms` },
  { value: "monitoring", label: msg`Monitoring an existing condition` },
  { value: "doctor_recommendation", label: msg`Doctor recommendation` },
  { value: "preventative", label: msg`Preventative / longevity screening` },
  { value: "pre_procedure", label: msg`Pre-procedure clearance` },
  { value: "other", label: msg`Other` },
];

const DIAGNOSTICS_LAB_SYMPTOMS_OPTIONS = [
  { value: "fatigue", label: msg`Fatigue` },
  { value: "weight_changes", label: msg`Weight changes` },
  { value: "pain", label: msg`Pain or inflammation` },
  { value: "digestive", label: msg`Digestive issues` },
  { value: "hormonal", label: msg`Hormonal symptoms` },
  { value: "dizziness", label: msg`Dizziness or headaches` },
  { value: "sleep", label: msg`Sleep issues` },
  { value: "other", label: msg`Other` },
];

const DIAGNOSTICS_LAB_CONDITIONS_OPTIONS = [
  { value: "diabetes", label: msg`Diabetes` },
  { value: "thyroid", label: msg`Thyroid condition` },
  { value: "hormonal_imbalance", label: msg`Hormonal imbalance` },
  { value: "autoimmune", label: msg`Autoimmune condition` },
  { value: "cardiovascular", label: msg`Cardiovascular condition` },
  { value: "anemia", label: msg`Anemia or blood disorder` },
  { value: "none", label: msg`None` },
  { value: "other", label: msg`Other` },
];

const DIAGNOSTICS_LAB_DOCTOR_STATUS_OPTIONS = [
  { value: "yes_list", label: msg`Yes, I have a list` },
  { value: "yes_no_list", label: msg`Yes, but no specific list` },
  { value: "no_guidance", label: msg`No, I want guidance` },
];

const DIAGNOSTICS_LAB_SCOPE_OPTIONS = [
  { value: "specific", label: msg`Specific tests only` },
  { value: "comprehensive", label: msg`Comprehensive panel` },
  { value: "unsure", label: msg`Unsure – want recommendations` },
];

const DIAGNOSTICS_LAB_URGENCY_OPTIONS = [
  { value: "asap", label: msg`As soon as possible` },
  { value: "1_2_weeks", label: msg`Within 1–2 weeks` },
  { value: "flexible", label: msg`Flexible` },
];

const DIAGNOSTICS_LAB_RESULTS_USAGE_OPTIONS = [
  { value: "doctor_review", label: msg`Review with my doctor` },
  { value: "medical_travel", label: msg`Use for medical travel planning` },
  { value: "preventative", label: msg`Preventative monitoring` },
  { value: "personal_tracking", label: msg`Personal health tracking` },
  { value: "unsure", label: msg`Not sure yet` },
];

const DIAGNOSTICS_LAB_FOLLOWUP_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
  { value: "maybe", label: msg`Maybe, depending on results` },
];

// Fertility Constants
const FERTILITY_CARE_FOCUS_OPTIONS = [
  { value: "evaluation", label: msg`Fertility evaluation` },
  { value: "ivf", label: msg`IVF` },
  { value: "egg_freezing", label: msg`Egg freezing` },
  { value: "sperm_analysis", label: msg`Sperm analysis` },
  { value: "hormonal", label: msg`Hormonal assessment` },
  { value: "other", label: msg`Other` },
];

const FERTILITY_PATIENT_TYPE_OPTIONS = [
  { value: "individual", label: msg`Individual` },
  { value: "couple", label: msg`Couple` },
];

const FERTILITY_PREVIOUS_ATTEMPTS_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
];

const FERTILITY_KNOWN_DIAGNOSIS_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
  { value: "unsure", label: msg`Unsure` },
];

const FERTILITY_TIMELINE_OPTIONS = [
  { value: "immediately", label: msg`Immediately` },
  { value: "months_3_6", label: msg`Within 3–6 months` },
  { value: "planning", label: msg`Planning stage` },
];

/** Maps API / legacy string or string[] answers onto checkbox state + free-text "other". */
function normalizeMultiOptionField(
  raw: string | string[] | undefined,
  validValues: readonly string[],
): { selected: string[]; other: string } {
  if (!raw) return { selected: [], other: "" };
  const arr = Array.isArray(raw) ? raw : [raw];
  const validSet = new Set(validValues);
  const selected: string[] = [];
  const unknown: string[] = [];
  for (const item of arr) {
    if (validSet.has(item)) selected.push(item);
    else unknown.push(item);
  }
  if (unknown.length === 0) return { selected, other: "" };
  return {
    selected: selected.includes("other") ? selected : [...selected, "other"],
    other: unknown.join(", "),
  };
}

function multiSelectWithOtherToPayload(
  selected: string[],
  otherText: string,
): string[] {
  if (selected.includes("other")) {
    return [
      ...selected.filter((v) => v !== "other"),
      ...(otherText.trim() ? [otherText.trim()] : []),
    ];
  }
  return selected;
}

// Wellness Constants
const WELLNESS_PRIMARY_GOAL_OPTIONS = [
  { value: "pain_relief", label: msg`Pain relief` },
  { value: "improved_mobility", label: msg`Improved mobility` },
  { value: "general_wellness", label: msg`General wellness` },
  { value: "other", label: msg`Other` },
];

const ALL_WELLNESS_PRIMARY_GOAL_VALID_VALUES = Array.from(
  new Set([
    ...WELLNESS_HEALTHCARE_GOAL_OPTIONS.map((o) => o.value),
    ...WELLNESS_PRIMARY_GOAL_OPTIONS.map((o) => o.value),
  ]),
);

const WELLNESS_TIMELINE_OPTIONS = [
  { value: "asap", label: msg`ASAP` },
  { value: "months_3_6", label: msg`Within 3–6 months` },
  { value: "planning", label: msg`Planning stage` },
  { value: "flexible", label: msg`Flexible` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_PREVIOUS_EXPERIENCE_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
  { value: "unsure", label: msg`Unsure` },
];

const WELLNESS_TARGET_AREA_OPTIONS = [
  { value: "back", label: msg`Back (body)` },
  { value: "neck", label: msg`Neck` },
  { value: "shoulders", label: msg`Shoulders` },
  { value: "abdomen", label: msg`Abdomen` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_SESSION_FREQUENCY_OPTIONS = [
  { value: "once_week", label: msg`Once a week` },
  { value: "twice_week", label: msg`Twice a week` },
  { value: "weekly", label: msg`Weekly` },
  { value: "flexible", label: msg`Flexible` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_HYPERBARIC_REASON_OPTIONS = [
  { value: "recovery", label: msg`Recovery` },
  { value: "wound_healing", label: msg`Wound healing` },
  { value: "sports_recovery", label: msg`Sports recovery` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_HYPERBARIC_URGENCY_OPTIONS = [
  { value: "immediately", label: msg`Immediately` },
  { value: "within_1_2_months", label: msg`Within 1–2 months` },
  { value: "flexible", label: msg`Flexible` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_HYPERBARIC_SESSIONS_OPTIONS = [
  { value: "5_10", label: msg`5-10 sessions` },
  { value: "10_15", label: msg`10-15 sessions` },
  { value: "20_30", label: msg`20-30 sessions` },
  { value: "30_plus", label: msg`More than 30 sessions` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_MASSAGE_FOCUS_OPTIONS = [
  { value: "relaxation", label: msg`Relaxation` },
  { value: "pain_relief", label: msg`Pain relief` },
  { value: "mobility_improvement", label: msg`Improved mobility` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_MASSAGE_PRESSURE_OPTIONS = [
  { value: "gentle", label: msg`Gentle` },
  { value: "moderate", label: msg`Moderate` },
  { value: "deep", label: msg`Deep` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_REFLEXOLOGY_TARGET_OPTIONS = [
  { value: "feet", label: msg`Feet` },
  { value: "hands", label: msg`Hands` },
  { value: "whole_body", label: msg`Whole body` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_REFLEXOLOGY_SYMPTOMS_OPTIONS = [
  { value: "fatigue", label: msg`Fatigue` },
  { value: "pain", label: msg`Pain` },
  { value: "digestive", label: msg`Digestive issues` },
  { value: "hormonal", label: msg`Hormonal symptoms` },
  { value: "sleep", label: msg`Sleep issues` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_OZONE_REASON_OPTIONS = [
  { value: "inflammation", label: msg`Inflammation` },
  { value: "recovery", label: msg`Recovery` },
  { value: "immunity", label: msg`Immune support` },
  { value: "other", label: msg`Other` },
];

const WELLNESS_FUNCTIONAL_DOCTOR_PROGRAM_OPTIONS = [
  {
    value: "chinese_medicine_acupuncture",
    label: msg`Chinese Medicine (with acupuncture)`,
  },
  { value: "quantum_healing", label: msg`Quantum Healing` },
  { value: "iv_therapies", label: msg`IV Therapies` },
  { value: "yoga_8am", label: msg`Yoga M to F 8AM` },
  { value: "other", label: msg`Other` },
];

// Bariatric: normalization only (legacy API values for fields not shown in current form)
const BARIATRIC_PREVIOUS_ATTEMPTS_OPTIONS = [
  { value: "diet_exercise", label: msg`Diet & exercise` },
  { value: "structured_program", label: msg`Structured program` },
  { value: "medications", label: msg`Weight-loss medications` },
  { value: "none", label: msg`None` },
  { value: "other", label: msg`Other` },
];

const BARIATRIC_TIMELINE_OPTIONS = [
  { value: "asap", label: msg`ASAP` },
  { value: "months_3_6", label: msg`Within 3–6 months` },
  { value: "months_6_12", label: msg`Within 6–12 months` },
  { value: "planning", label: msg`Planning stage` },
  { value: "flexible", label: msg`Flexible` },
  { value: "other", label: msg`Other` },
];

// Stem Cell Constants
const STEM_CELL_REASON_OPTIONS = [
  { value: "joint_pain", label: msg`Joint pain` },
  { value: "arthritis", label: msg`Arthritis` },
  { value: "sports_injury", label: msg`Sports injury` },
  { value: "neurological", label: msg`Neurological condition` },
  { value: "autoimmune", label: msg`Autoimmune condition` },
  { value: "anti_aging", label: msg`Anti-aging / wellness` },
  { value: "other", label: msg`Other` },
];

const STEM_CELL_LOCATION_OPTIONS = [
  { value: "knee", label: msg`Knee` },
  { value: "shoulder", label: msg`Shoulder` },
  { value: "hip", label: msg`Hip` },
  { value: "spine", label: msg`Spine` },
  { value: "multiple", label: msg`Multiple joints` },
  { value: "whole_body", label: msg`Whole-body / systemic` },
];

const STEM_CELL_DIAGNOSIS_OPTIONS = [
  { value: "yes", label: msg`Yes` },
  { value: "no", label: msg`No` },
  { value: "unsure", label: msg`Unsure` },
];

const STEM_CELL_GOAL_OPTIONS = [
  { value: "pain_reduction", label: msg`Pain reduction` },
  { value: "mobility", label: msg`Improved mobility` },
  { value: "degeneration", label: msg`Slowed degeneration` },
  { value: "recovery", label: msg`Recovery after injury` },
  { value: "wellness", label: msg`General wellness` },
];

const STEM_CELL_PRIOR_TREATMENT_OPTIONS = [
  { value: "physical_therapy", label: msg`Physical therapy` },
  { value: "injections", label: msg`Injections` },
  { value: "surgery", label: msg`Surgery` },
  { value: "none", label: msg`None` },
];


export default function DetailedQuestionsStep({
  data,
  onUpdate,
  onNext,
  onBack,
}: DetailedQuestionsStepProps) {
  const { i18n } = useLingui();
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Helper to safely get procedure slug
  const getProcedureSlug = (p: IntakeData['procedures']) => {
    if (typeof p === 'string') return p;
    return p?.slug || '';
  };

  const procedureSlug = getProcedureSlug(data.procedures);

  const isVascular = procedureSlug === "vascular";
  const isHairTransplant = procedureSlug.includes("hair") || procedureSlug === "hair-transplant";
  const isOptical = procedureSlug.includes("optical") || procedureSlug === "optics";
  const isPlasticSurgery = procedureSlug.includes("plastic") || procedureSlug === "cosmetic";
  const isDiagnostics = procedureSlug.includes("diagnostic") || procedureSlug.includes("imaging") || procedureSlug === "radiology";
  const isFertility = procedureSlug === "fertility";
  const isStemCell = procedureSlug.includes("stem");
  const isBariatric = procedureSlug === "bariatric" || procedureSlug.includes("bariatric");
  const isWellness =
    procedureSlug.includes("wellness") ||
    procedureSlug.includes("acupuncture") ||
    procedureSlug.includes("colema") ||
    procedureSlug.includes("hyperbaric") ||
    procedureSlug.includes("massage") ||
    procedureSlug.includes("reflexology") ||
    procedureSlug.includes("ozone") ||
    procedureSlug.includes("functional") ||
    procedureSlug.includes("quantum") ||
    procedureSlug.includes("iv") ||
    procedureSlug.includes("yoga") ||
    procedureSlug.includes("chinese");
  /** General "Wellness & Healthcare" procedure only — not acupuncture/massage/etc. slugs */
  const isWellnessHealthcareSlug =
    procedureSlug === "wellness_healthcare" || procedureSlug === "wellness-healthcare";

  const isAcupuncture =
    procedureSlug.includes("acupuncture") || procedureSlug.includes("colema");
  const isHyperbaric = procedureSlug.includes("hyperbaric");
  const isMassage = procedureSlug.includes("massage");
  const isReflexology = procedureSlug.includes("reflexology");
  const isOzone = procedureSlug.includes("ozone");
  const isFunctionalDoctor =
    procedureSlug.includes("functional") ||
    procedureSlug.includes("quantum") ||
    procedureSlug.includes("iv") ||
    procedureSlug.includes("yoga") ||
    procedureSlug.includes("chinese");

  const [formData, setFormData] = useState({
    // Vascular fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.vascularPrimaryGoal,
        PRIMARY_GOAL_OPTIONS.map((o) => o.value),
      );
      return {
        vascularPrimaryGoal: n.selected,
        vascularPrimaryGoalOther: n.other,
      };
    })(),
    vascularSymptoms: data.vascularSymptoms || "",
    vascularSymptomTypes: data.vascularSymptomTypes || ([] as string[]),
    vascularSymptomOther: "",
    vascularDiagnosisStatus: data.vascularDiagnosisStatus || "",
    vascularUrgency: data.vascularUrgency || "",
    vascularProceduralExpectation: data.vascularProceduralExpectation || "",
    // Hair transplant fields
    hairTransplantRelevantFactors: data.hairTransplantRelevantFactors || ([] as string[]),
    hairTransplantTreatmentAreas: data.hairTransplantTreatmentAreas || ([] as string[]),
    hairTransplantTreatmentAreasOther: data.hairTransplantTreatmentAreasOther || "",
    hairTransplantProgression: data.hairTransplantProgression || "",
    hairTransplantPreviousTreatments: data.hairTransplantPreviousTreatments || ([] as string[]),
    hairTransplantOutcomeExpectation: data.hairTransplantOutcomeExpectation || "",
    hairTransplantTimeline: data.hairTransplantTimeline || "",
    // Optical fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.opticalPrimaryReason,
        OPTICAL_REASON_OPTIONS.map((o) => o.value),
      );
      return {
        opticalPrimaryReason: n.selected,
        opticalPrimaryReasonOther: n.other,
      };
    })(),
    opticalVisionCorrection: data.opticalVisionCorrection || "",
    opticalLaterality: data.opticalLaterality || "",
    opticalSymptoms: data.opticalSymptoms || ([] as string[]),
    opticalProcedureInterest: data.opticalProcedureInterest || "",
    // Plastic Surgery fields
    plasticAreaOfInterest: data.plasticAreaOfInterest || "",
    plasticAreaOfInterestOther: "",
    plasticProcedureType: data.plasticProcedureType || "",
    plasticProcedureTypeOther: "",
    plasticSpecificConcern: data.plasticSpecificConcern || "",
    plasticSpecificConcernOther: "",
    plasticPreviousSurgery: data.plasticPreviousSurgery || "",
    plasticOutcomePriority: data.plasticOutcomePriority || "",
    plasticOutcomePriorityOther: "",
    // Diagnostics fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.diagnosticsPurpose,
        DIAGNOSTICS_PURPOSE_OPTIONS.map((o) => o.value),
      );
      return {
        diagnosticsPurpose: n.selected,
        diagnosticsPurposeOther: n.other,
      };
    })(),
    diagnosticsType: data.diagnosticsType || "",
    diagnosticsArea: data.diagnosticsArea || "",
    diagnosticsAreaOther: "",

    diagnosticsTimeSensitivity: data.diagnosticsTimeSensitivity || "",
    // Lab Diagnostics fields
    diagnosticsCategory: data.diagnosticsCategory || "",
    diagnosticsReason: data.diagnosticsReason || "",
    diagnosticsReasonOther: "",
    diagnosticsHasSymptoms: data.diagnosticsHasSymptoms || "",
    diagnosticsSymptoms: data.diagnosticsSymptoms || ([] as string[]),
    diagnosticsSymptomOther: "",
    diagnosticsConditions: data.diagnosticsConditions || ([] as string[]),
    diagnosticsConditionOther: "",
    diagnosticsDoctorStatus: data.diagnosticsDoctorStatus || "",
    diagnosticsDoctorList: data.diagnosticsDoctorList || "",
    diagnosticsScope: data.diagnosticsScope || "",
    diagnosticsRecentTesting: data.diagnosticsRecentTesting || "",
    diagnosticsShareRecentResults: data.diagnosticsShareRecentResults || "",
    diagnosticsLabUrgency: data.diagnosticsLabUrgency || "",
    diagnosticsFasting: data.diagnosticsFasting || "",
    diagnosticsResultsUsage: data.diagnosticsResultsUsage || "",
    diagnosticsFollowUp: data.diagnosticsFollowUp || "",

    // Fertility fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.fertilityCareFocus,
        FERTILITY_CARE_FOCUS_OPTIONS.map((o) => o.value),
      );
      return {
        fertilityCareFocus: n.selected,
        fertilityCareFocusOther: n.other,
      };
    })(),
    fertilityPatientType: data.fertilityPatientType || "",
    fertilityPreviousAttempts: data.fertilityPreviousAttempts || "",
    fertilityReference: data.fertilityKnownDiagnosis || "",
    fertilityKnownDiagnosis: data.fertilityKnownDiagnosis || "",
    fertilityTimeline: data.fertilityTimeline || "",
    // Stem Cell fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.stemCellReason,
        STEM_CELL_REASON_OPTIONS.map((o) => o.value),
      );
      return {
        stemCellReason: n.selected,
        stemCellReasonOther: n.other,
      };
    })(),
    stemCellLocation: data.stemCellLocation || "",
    stemCellDiagnosisStatus: data.stemCellDiagnosisStatus || "",
    stemCellTreatmentGoal: data.stemCellTreatmentGoal || "",
    stemCellPriorTreatments: data.stemCellPriorTreatments || ([] as string[]),

    // Bariatric fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.bariatricPrimaryGoal,
        BARIATRIC_GOAL_OPTIONS.map((o) => o.value),
      );
      return {
        bariatricPrimaryGoal: n.selected,
        bariatricPrimaryGoalOther: n.other,
      };
    })(),
    bariatricPriorWeightLoss: data.bariatricPriorWeightLoss || ([] as string[]),
    bariatricProcedureInterest: data.bariatricProcedureInterest || "",
    bariatricPreviousAttempts: data.bariatricPreviousAttempts || ([] as string[]),
    bariatricPreviousAttemptsOther: data.bariatricPreviousAttemptsOther || "",
    bariatricCurrentDiet: data.bariatricCurrentDiet || "",
    bariatricCurrentDietOther: "",
    bariatricTimeline: data.bariatricTimeline || "",
    bariatricTimelineOther: data.bariatricTimelineOther || "",

    // Wellness fields
    ...(() => {
      const n = normalizeMultiOptionField(
        data.wellnessPrimaryGoal,
        ALL_WELLNESS_PRIMARY_GOAL_VALID_VALUES,
      );
      return {
        wellnessPrimaryGoal: n.selected,
        wellnessPrimaryGoalOther: n.other,
      };
    })(),
    wellnessCurrentPractices: data.wellnessCurrentPractices || ([] as string[]),
    wellnessSpecificConcerns: data.wellnessSpecificConcerns || "",
    wellnessSpecificConcernsOther: "",
    wellnessTimeline: data.wellnessTimeline || "",
    wellnessTimelineOther: data.wellnessTimelineOther || "",
    wellnessPreviousExperience: data.wellnessPreviousExperience || "",
    wellnessAdditionalNotes: data.wellnessAdditionalNotes || "",

    wellnessAcupunctureConcern: data.wellnessAcupunctureConcern || "",
    wellnessAcupunctureConcernOther: data.wellnessAcupunctureConcernOther || "",
    wellnessAcupunctureTargetArea: data.wellnessAcupunctureTargetArea || "",
    wellnessAcupunctureFrequency: data.wellnessAcupunctureFrequency || "",

    wellnessHyperbaricReason: data.wellnessHyperbaricReason || "",
    wellnessHyperbaricReasonOther: data.wellnessHyperbaricReasonOther || "",
    wellnessHyperbaricUrgency: data.wellnessHyperbaricUrgency || "",
    wellnessHyperbaricSessions: data.wellnessHyperbaricSessions || "",

    wellnessMassageFocus: data.wellnessMassageFocus || "",
    wellnessMassageFocusOther: data.wellnessMassageFocusOther || "",
    wellnessMassagePressure: data.wellnessMassagePressure || "",

    wellnessReflexologyTarget: data.wellnessReflexologyTarget || "",
    wellnessReflexologyMainSymptoms: data.wellnessReflexologyMainSymptoms || ([] as string[]),
    wellnessReflexologyMainSymptomsOther: data.wellnessReflexologyMainSymptomsOther || "",

    wellnessOzoneReason: data.wellnessOzoneReason || "",
    wellnessOzoneReasonOther: data.wellnessOzoneReasonOther || "",
    wellnessOzonePreviousExperience: data.wellnessOzonePreviousExperience || "",

    wellnessFunctionalDoctorProgram: data.wellnessFunctionalDoctorProgram || "",
    wellnessFunctionalDoctorProgramOther: data.wellnessFunctionalDoctorProgramOther || "",
    wellnessFunctionalDoctorExperience: data.wellnessFunctionalDoctorExperience || "",
  });

  useEffect(() => {
    if (!isWellnessHealthcareSlug) return;
    if (data.wellnessSpecificConcerns && !WELLNESS_HEALTHCARE_CONCERNS_OPTIONS.some(o => o.value === data.wellnessSpecificConcerns)) {
       setFormData(prev => ({ ...prev, wellnessSpecificConcerns: "other", wellnessSpecificConcernsOther: data.wellnessSpecificConcerns || "" }));
    }
  }, [data.wellnessSpecificConcerns, isWellnessHealthcareSlug]);

  useEffect(() => {
    if (data.bariatricCurrentDiet && !BARIATRIC_DIET_OPTIONS.some(o => o.value === data.bariatricCurrentDiet)) {
       setFormData(prev => ({ ...prev, bariatricCurrentDiet: "other", bariatricCurrentDietOther: data.bariatricCurrentDiet || "" }));
    }
  }, [data.bariatricCurrentDiet]);

  useEffect(() => {
    if (data.plasticAreaOfInterest && !PLASTIC_AREA_OPTIONS.some(o => o.value === data.plasticAreaOfInterest)) {
       setFormData(prev => ({ ...prev, plasticAreaOfInterest: "other", plasticAreaOfInterestOther: data.plasticAreaOfInterest || "" }));
    }
  }, [data.plasticAreaOfInterest]);

  useEffect(() => {
    if (data.plasticProcedureType && !PLASTIC_PROCEDURE_TYPE_OPTIONS.some(o => o.value === data.plasticProcedureType)) {
       setFormData(prev => ({ ...prev, plasticProcedureType: "other", plasticProcedureTypeOther: data.plasticProcedureType || "" }));
    }
  }, [data.plasticProcedureType]);

  useEffect(() => {
    if (data.plasticSpecificConcern && !PLASTIC_CONCERN_OPTIONS.some(o => o.value === data.plasticSpecificConcern)) {
       setFormData(prev => ({ ...prev, plasticSpecificConcern: "other", plasticSpecificConcernOther: data.plasticSpecificConcern || "" }));
    }
  }, [data.plasticSpecificConcern]);

  useEffect(() => {
    if (data.plasticOutcomePriority && !PLASTIC_OUTCOME_OPTIONS.some(o => o.value === data.plasticOutcomePriority)) {
       setFormData(prev => ({ ...prev, plasticOutcomePriority: "other", plasticOutcomePriorityOther: data.plasticOutcomePriority || "" }));
    }
  }, [data.plasticOutcomePriority]);

  // Force sync Hair Transplant fields
  useEffect(() => {
    if (isHairTransplant) {
        setFormData(prev => ({
            ...prev,
            hairTransplantRelevantFactors: data.hairTransplantRelevantFactors || [],
            hairTransplantTreatmentAreas: data.hairTransplantTreatmentAreas || [],
            hairTransplantTreatmentAreasOther: data.hairTransplantTreatmentAreasOther || "",
            hairTransplantProgression: data.hairTransplantProgression || "",
            hairTransplantPreviousTreatments: data.hairTransplantPreviousTreatments || [],
            hairTransplantOutcomeExpectation: data.hairTransplantOutcomeExpectation || "",
            hairTransplantTimeline: data.hairTransplantTimeline || "",
        }));
    } else {
        // Clear if not active procedure
        setFormData(prev => ({
            ...prev,
            hairTransplantRelevantFactors: [],
            hairTransplantTreatmentAreas: [],
            hairTransplantTreatmentAreasOther: "",
            hairTransplantProgression: "",
            hairTransplantPreviousTreatments: [],
            hairTransplantOutcomeExpectation: "",
            hairTransplantTimeline: "",
        }));
    }
  }, [
      isHairTransplant,
      data.hairTransplantRelevantFactors,
      data.hairTransplantTreatmentAreas,
      data.hairTransplantTreatmentAreasOther,
      data.hairTransplantProgression,
      data.hairTransplantPreviousTreatments,
      data.hairTransplantOutcomeExpectation,
      data.hairTransplantTimeline
  ]);

  // Sync Vascular fields
  useEffect(() => {
    if (isVascular) {
        const primary = normalizeMultiOptionField(
          data.vascularPrimaryGoal,
          PRIMARY_GOAL_OPTIONS.map((o) => o.value),
        );
        setFormData((prev) => ({
        ...prev,
        vascularPrimaryGoal: primary.selected,
        vascularPrimaryGoalOther: primary.other,
        vascularSymptoms: data.vascularSymptoms || "",
        vascularSymptomTypes: data.vascularSymptomTypes || [],
        vascularDiagnosisStatus: data.vascularDiagnosisStatus || "",
        vascularUrgency: data.vascularUrgency || "",
        vascularProceduralExpectation: data.vascularProceduralExpectation || "",
        }));
    } else {
        setFormData((prev) => ({
            ...prev,
            vascularPrimaryGoal: [],
            vascularPrimaryGoalOther: "",
            vascularSymptoms: "",
            vascularSymptomTypes: [],
            vascularDiagnosisStatus: "",
            vascularUrgency: "",
            vascularProceduralExpectation: "",
        }));
    }
  }, [isVascular, data.vascularPrimaryGoal, data.vascularSymptoms, data.vascularSymptomTypes, data.vascularDiagnosisStatus, data.vascularUrgency, data.vascularProceduralExpectation]);

  // Sync Optical fields
  useEffect(() => {
    if (isOptical) {
        const reason = normalizeMultiOptionField(
          data.opticalPrimaryReason,
          OPTICAL_REASON_OPTIONS.map((o) => o.value),
        );
        setFormData((prev) => ({
        ...prev,
        opticalPrimaryReason: reason.selected,
        opticalPrimaryReasonOther: reason.other,
        opticalVisionCorrection: data.opticalVisionCorrection || "",
        opticalLaterality: data.opticalLaterality || "",
        opticalSymptoms: data.opticalSymptoms || [],
        opticalProcedureInterest: data.opticalProcedureInterest || "",
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            opticalPrimaryReason: [],
            opticalPrimaryReasonOther: "",
            opticalVisionCorrection: "",
            opticalLaterality: "",
            opticalSymptoms: [],
            opticalProcedureInterest: "",
        }));
    }
  }, [isOptical, data.opticalPrimaryReason, data.opticalVisionCorrection, data.opticalLaterality, data.opticalSymptoms, data.opticalProcedureInterest]);

  // Sync Plastic Surgery fields
  useEffect(() => {
    if (isPlasticSurgery) {
        setFormData(prev => ({
        ...prev,
        plasticAreaOfInterest: data.plasticAreaOfInterest || "",
        plasticProcedureType: data.plasticProcedureType || "",
        plasticSpecificConcern: data.plasticSpecificConcern || "",
        plasticPreviousSurgery: data.plasticPreviousSurgery || "",
        plasticOutcomePriority: data.plasticOutcomePriority || "",
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            plasticAreaOfInterest: "",
            plasticProcedureType: "",
            plasticSpecificConcern: "",
            plasticPreviousSurgery: "",
            plasticOutcomePriority: "",
        }));
    }
  }, [isPlasticSurgery, data.plasticAreaOfInterest, data.plasticProcedureType, data.plasticSpecificConcern, data.plasticPreviousSurgery, data.plasticOutcomePriority]);

  // Sync Diagnostics fields
  useEffect(() => {
    if (isDiagnostics) {
        const purpose = normalizeMultiOptionField(
          data.diagnosticsPurpose,
          DIAGNOSTICS_PURPOSE_OPTIONS.map((o) => o.value),
        );
        setFormData((prev) => ({
        ...prev,
        diagnosticsPurpose: purpose.selected,
        diagnosticsPurposeOther: purpose.other,
        diagnosticsType: data.diagnosticsType || "",
        diagnosticsArea: data.diagnosticsArea || "",
        diagnosticsTimeSensitivity: data.diagnosticsTimeSensitivity || "",
        diagnosticsCategory: data.diagnosticsCategory || "",
        diagnosticsReason: data.diagnosticsReason || "",
        diagnosticsHasSymptoms: data.diagnosticsHasSymptoms || "",
        diagnosticsSymptoms: data.diagnosticsSymptoms || [],
        diagnosticsConditions: data.diagnosticsConditions || [],
        diagnosticsDoctorStatus: data.diagnosticsDoctorStatus || "",
        diagnosticsDoctorList: data.diagnosticsDoctorList || "",
        diagnosticsScope: data.diagnosticsScope || "",
        diagnosticsRecentTesting: data.diagnosticsRecentTesting || "",
        diagnosticsShareRecentResults: data.diagnosticsShareRecentResults || "",
        diagnosticsLabUrgency: data.diagnosticsLabUrgency || "",
        diagnosticsFasting: data.diagnosticsFasting || "",
        diagnosticsResultsUsage: data.diagnosticsResultsUsage || "",
        diagnosticsFollowUp: data.diagnosticsFollowUp || "",
        }));
    } else {
        setFormData((prev) => ({
            ...prev,
            diagnosticsPurpose: [],
            diagnosticsPurposeOther: "",
            diagnosticsType: "",
            diagnosticsArea: "",
            diagnosticsTimeSensitivity: "",
            diagnosticsCategory: "",
            diagnosticsReason: "",
            diagnosticsHasSymptoms: "",
            diagnosticsSymptoms: [],
            diagnosticsConditions: [],
            diagnosticsDoctorStatus: "",
            diagnosticsDoctorList: "",
            diagnosticsScope: "",
            diagnosticsRecentTesting: "",
            diagnosticsShareRecentResults: "",
            diagnosticsLabUrgency: "",
            diagnosticsFasting: "",
            diagnosticsResultsUsage: "",
            diagnosticsFollowUp: "",
        }));
    }
  }, [
    isDiagnostics, 
    data.diagnosticsPurpose, data.diagnosticsType, data.diagnosticsArea, data.diagnosticsTimeSensitivity,
    data.diagnosticsCategory, data.diagnosticsReason, data.diagnosticsHasSymptoms, data.diagnosticsSymptoms,
    data.diagnosticsConditions, data.diagnosticsDoctorStatus, data.diagnosticsDoctorList, data.diagnosticsScope,
    data.diagnosticsRecentTesting, data.diagnosticsShareRecentResults, data.diagnosticsLabUrgency,
    data.diagnosticsFasting, data.diagnosticsResultsUsage, data.diagnosticsFollowUp
  ]);

  useEffect(() => {
    if (data.diagnosticsReason && !DIAGNOSTICS_LAB_REASON_OPTIONS.some(o => o.value === data.diagnosticsReason)) {
       setFormData(prev => ({ ...prev, diagnosticsReason: "other", diagnosticsReasonOther: data.diagnosticsReason || "" }));
    }
  }, [data.diagnosticsReason]);

  // Handle other fields for Lab Diagnostics...
  useEffect(() => {
    if (data.diagnosticsArea && !DIAGNOSTICS_AREA_OPTIONS.some(o => o.value === data.diagnosticsArea)) {
       setFormData(prev => ({ ...prev, diagnosticsArea: "other", diagnosticsAreaOther: data.diagnosticsArea || "" }));
    }
  }, [data.diagnosticsArea]);

  // Sync Fertility fields
  useEffect(() => {
    if (isFertility) {
        const care = normalizeMultiOptionField(
          data.fertilityCareFocus,
          FERTILITY_CARE_FOCUS_OPTIONS.map((o) => o.value),
        );
        setFormData((prev) => ({
        ...prev,
        fertilityCareFocus: care.selected,
        fertilityCareFocusOther: care.other,
        fertilityPatientType: data.fertilityPatientType || "",
        fertilityPreviousAttempts: data.fertilityPreviousAttempts || "",
        fertilityKnownDiagnosis: data.fertilityKnownDiagnosis || "",
        fertilityTimeline: data.fertilityTimeline || "",
        }));
    } else {
        setFormData((prev) => ({
            ...prev,
            fertilityCareFocus: [],
            fertilityCareFocusOther: "",
            fertilityPatientType: "",
            fertilityPreviousAttempts: "",
            fertilityKnownDiagnosis: "",
            fertilityTimeline: "",
        }));
    }
  }, [isFertility, data.fertilityCareFocus, data.fertilityPatientType, data.fertilityPreviousAttempts, data.fertilityKnownDiagnosis, data.fertilityTimeline]);

  // Sync Stem Cell fields
  useEffect(() => {
    if (isStemCell) {
        const reason = normalizeMultiOptionField(
          data.stemCellReason,
          STEM_CELL_REASON_OPTIONS.map((o) => o.value),
        );
        setFormData((prev) => ({
        ...prev,
        stemCellReason: reason.selected,
        stemCellReasonOther: reason.other,
        stemCellLocation: data.stemCellLocation || "",
        stemCellDiagnosisStatus: data.stemCellDiagnosisStatus || "",
        stemCellTreatmentGoal: data.stemCellTreatmentGoal || "",
        stemCellPriorTreatments: data.stemCellPriorTreatments || [],
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            stemCellReason: [],
            stemCellReasonOther: "",
            stemCellLocation: "",
            stemCellDiagnosisStatus: "",
            stemCellTreatmentGoal: "",
            stemCellPriorTreatments: [],
        }));
    }
  }, [isStemCell, data.stemCellReason, data.stemCellLocation, data.stemCellDiagnosisStatus, data.stemCellTreatmentGoal, data.stemCellPriorTreatments]);

  // Sync Bariatric fields
  useEffect(() => {
    if (isBariatric) {
      const primary = normalizeMultiOptionField(
        data.bariatricPrimaryGoal,
        BARIATRIC_GOAL_OPTIONS.map((o) => o.value),
      );
      setFormData((prev) => ({
        ...prev,
        bariatricPrimaryGoal: primary.selected,
        bariatricPrimaryGoalOther: primary.other,
        bariatricPriorWeightLoss: data.bariatricPriorWeightLoss || ([] as string[]),
        bariatricProcedureInterest: data.bariatricProcedureInterest || "",
        bariatricPreviousAttempts: data.bariatricPreviousAttempts || ([] as string[]),
        bariatricPreviousAttemptsOther: data.bariatricPreviousAttemptsOther || "",
        bariatricCurrentDiet: data.bariatricCurrentDiet || "",
        bariatricTimeline: data.bariatricTimeline || "",
        bariatricTimelineOther: data.bariatricTimelineOther || "",
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        bariatricPrimaryGoal: [],
        bariatricPrimaryGoalOther: "",
        bariatricPriorWeightLoss: [],
        bariatricProcedureInterest: "",
        bariatricPreviousAttempts: [],
        bariatricPreviousAttemptsOther: "",
        bariatricCurrentDiet: "",
        bariatricCurrentDietOther: "",
        bariatricTimeline: "",
        bariatricTimelineOther: "",
      }));
    }
  }, [
    isBariatric,
    data.bariatricPrimaryGoal,
    data.bariatricPrimaryGoalOther,
    data.bariatricPriorWeightLoss,
    data.bariatricProcedureInterest,
    data.bariatricPreviousAttempts,
    data.bariatricPreviousAttemptsOther,
    data.bariatricCurrentDiet,
    data.bariatricTimeline,
    data.bariatricTimelineOther,
  ]);

  useEffect(() => {
    if (!isBariatric) return;
    if (
      data.bariatricTimeline &&
      !BARIATRIC_TIMELINE_OPTIONS.some(o => o.value === data.bariatricTimeline)
    ) {
      setFormData(prev => ({
        ...prev,
        bariatricTimeline: "other",
        bariatricTimelineOther: data.bariatricTimeline || "",
      }));
    }
  }, [isBariatric, data.bariatricTimeline]);

  // Normalize "previous attempts" so free-text "other" becomes the `other` checkbox + text input
  useEffect(() => {
    if (!isBariatric) return;

    const selected = Array.isArray(data.bariatricPreviousAttempts)
      ? data.bariatricPreviousAttempts
      : [];

    if (selected.length === 0) return;

    const optionValues = BARIATRIC_PREVIOUS_ATTEMPTS_OPTIONS.map(o => o.value);
    const unknown = selected.filter(v => v && !optionValues.includes(v));

    if (unknown.length > 0) {
      setFormData(prev => ({
        ...prev,
        bariatricPreviousAttempts: Array.from(
          new Set([...selected.filter(v => optionValues.includes(v) && v !== "other"), "other"])
        ),
        bariatricPreviousAttemptsOther: String(unknown[0]),
      }));
    }
  }, [isBariatric, data.bariatricPreviousAttempts]);

  const handleBariatricPriorWeightLossChange = (value: string, checked: boolean) => {
    if (value === 'none') {
      if (checked) {
        handleInputChange("bariatricPriorWeightLoss", ['none']);
      } else {
        handleInputChange("bariatricPriorWeightLoss", []);
      }
      return;
    }
    let current = [...(formData.bariatricPriorWeightLoss || [])];
    if (checked && current.includes('none')) {
      current = current.filter(v => v !== 'none');
    }
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("bariatricPriorWeightLoss", current);
  };

  const handleWellnessPracticesChange = (value: string, checked: boolean) => {
    if (value === 'none') {
      if (checked) {
        handleInputChange("wellnessCurrentPractices", ['none']);
      } else {
        handleInputChange("wellnessCurrentPractices", []);
      }
      return;
    }
    let current = [...(formData.wellnessCurrentPractices || [])];
    if (checked && current.includes('none')) {
      current = current.filter(v => v !== 'none');
    }
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("wellnessCurrentPractices", current);
  };

  // Sync Wellness fields
  useEffect(() => {
    if (isWellness) {
      const primary = normalizeMultiOptionField(
        data.wellnessPrimaryGoal,
        ALL_WELLNESS_PRIMARY_GOAL_VALID_VALUES,
      );
      setFormData((prev) => ({
        ...prev,
        wellnessPrimaryGoal: primary.selected,
        wellnessPrimaryGoalOther: primary.other,
        wellnessCurrentPractices: data.wellnessCurrentPractices || ([] as string[]),
        wellnessSpecificConcerns: data.wellnessSpecificConcerns || "",
        wellnessTimeline: data.wellnessTimeline || "",
        wellnessTimelineOther: data.wellnessTimelineOther || "",
        wellnessPreviousExperience: data.wellnessPreviousExperience || "",
        wellnessAdditionalNotes: data.wellnessAdditionalNotes || "",

        wellnessAcupunctureConcern: data.wellnessAcupunctureConcern || "",
        wellnessAcupunctureConcernOther: data.wellnessAcupunctureConcernOther || "",
        wellnessAcupunctureTargetArea: data.wellnessAcupunctureTargetArea || "",
        wellnessAcupunctureFrequency: data.wellnessAcupunctureFrequency || "",

        wellnessHyperbaricReason: data.wellnessHyperbaricReason || "",
        wellnessHyperbaricReasonOther: data.wellnessHyperbaricReasonOther || "",
        wellnessHyperbaricUrgency: data.wellnessHyperbaricUrgency || "",
        wellnessHyperbaricSessions: data.wellnessHyperbaricSessions || "",

        wellnessMassageFocus: data.wellnessMassageFocus || "",
        wellnessMassageFocusOther: data.wellnessMassageFocusOther || "",
        wellnessMassagePressure: data.wellnessMassagePressure || "",

        wellnessReflexologyTarget: data.wellnessReflexologyTarget || "",
        wellnessReflexologyMainSymptoms: data.wellnessReflexologyMainSymptoms || ([] as string[]),
        wellnessReflexologyMainSymptomsOther: data.wellnessReflexologyMainSymptomsOther || "",

        wellnessOzoneReason: data.wellnessOzoneReason || "",
        wellnessOzoneReasonOther: data.wellnessOzoneReasonOther || "",
        wellnessOzonePreviousExperience: data.wellnessOzonePreviousExperience || "",

        wellnessFunctionalDoctorProgram: data.wellnessFunctionalDoctorProgram || "",
        wellnessFunctionalDoctorProgramOther: data.wellnessFunctionalDoctorProgramOther || "",
        wellnessFunctionalDoctorExperience: data.wellnessFunctionalDoctorExperience || "",
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        wellnessPrimaryGoal: [],
        wellnessPrimaryGoalOther: "",
        wellnessCurrentPractices: [],
        wellnessSpecificConcerns: "",
        wellnessSpecificConcernsOther: "",
        wellnessTimeline: "",
        wellnessTimelineOther: "",
        wellnessPreviousExperience: "",
        wellnessAdditionalNotes: "",

        wellnessAcupunctureConcern: "",
        wellnessAcupunctureConcernOther: "",
        wellnessAcupunctureTargetArea: "",
        wellnessAcupunctureFrequency: "",

        wellnessHyperbaricReason: "",
        wellnessHyperbaricReasonOther: "",
        wellnessHyperbaricUrgency: "",
        wellnessHyperbaricSessions: "",

        wellnessMassageFocus: "",
        wellnessMassageFocusOther: "",
        wellnessMassagePressure: "",

        wellnessReflexologyTarget: "",
        wellnessReflexologyMainSymptoms: [],
        wellnessReflexologyMainSymptomsOther: "",

        wellnessOzoneReason: "",
        wellnessOzoneReasonOther: "",
        wellnessOzonePreviousExperience: "",

        wellnessFunctionalDoctorProgram: "",
        wellnessFunctionalDoctorProgramOther: "",
        wellnessFunctionalDoctorExperience: "",
      }));
    }
  }, [
    isWellness,
    data.wellnessPrimaryGoal,
    data.wellnessPrimaryGoalOther,
    data.wellnessCurrentPractices,
    data.wellnessSpecificConcerns,
    data.wellnessTimeline,
    data.wellnessTimelineOther,
    data.wellnessPreviousExperience,
    data.wellnessAdditionalNotes,
    data.wellnessAcupunctureConcern,
    data.wellnessAcupunctureConcernOther,
    data.wellnessAcupunctureTargetArea,
    data.wellnessAcupunctureFrequency,
    data.wellnessHyperbaricReason,
    data.wellnessHyperbaricReasonOther,
    data.wellnessHyperbaricUrgency,
    data.wellnessHyperbaricSessions,
    data.wellnessMassageFocus,
    data.wellnessMassageFocusOther,
    data.wellnessMassagePressure,
    data.wellnessReflexologyTarget,
    data.wellnessReflexologyMainSymptoms,
    data.wellnessReflexologyMainSymptomsOther,
    data.wellnessOzoneReason,
    data.wellnessOzoneReasonOther,
    data.wellnessOzonePreviousExperience,
    data.wellnessFunctionalDoctorProgram,
    data.wellnessFunctionalDoctorProgramOther,
    data.wellnessFunctionalDoctorExperience,
  ]);

  useEffect(() => {
    if (!isWellness || isWellnessHealthcareSlug) return;
    if (
      data.wellnessTimeline &&
      !WELLNESS_TIMELINE_OPTIONS.some(o => o.value === data.wellnessTimeline)
    ) {
      setFormData(prev => ({
        ...prev,
        wellnessTimeline: "other",
        wellnessTimelineOther: data.wellnessTimeline || "",
      }));
    }
  }, [isWellness, isWellnessHealthcareSlug, data.wellnessTimeline]);

  useEffect(() => {
    if (!isWellness || isWellnessHealthcareSlug) return;
    if (
      data.wellnessAcupunctureConcern &&
      !WELLNESS_PRIMARY_GOAL_OPTIONS.some(o => o.value === data.wellnessAcupunctureConcern)
    ) {
      setFormData(prev => ({
        ...prev,
        wellnessAcupunctureConcern: "other",
        wellnessAcupunctureConcernOther: data.wellnessAcupunctureConcern || "",
      }));
    }
  }, [isWellness, isWellnessHealthcareSlug, data.wellnessAcupunctureConcern]);

  useEffect(() => {
    if (!isWellness) return;
    if (
      data.wellnessHyperbaricReason &&
      !WELLNESS_HYPERBARIC_REASON_OPTIONS.some(o => o.value === data.wellnessHyperbaricReason)
    ) {
      setFormData(prev => ({
        ...prev,
        wellnessHyperbaricReason: "other",
        wellnessHyperbaricReasonOther: data.wellnessHyperbaricReason || "",
      }));
    }
  }, [isWellness, data.wellnessHyperbaricReason]);

  useEffect(() => {
    if (!isWellness) return;
    if (
      data.wellnessMassageFocus &&
      !WELLNESS_MASSAGE_FOCUS_OPTIONS.some(o => o.value === data.wellnessMassageFocus)
    ) {
      setFormData(prev => ({
        ...prev,
        wellnessMassageFocus: "other",
        wellnessMassageFocusOther: data.wellnessMassageFocus || "",
      }));
    }
  }, [isWellness, data.wellnessMassageFocus]);

  useEffect(() => {
    if (!isWellness) return;
    if (
      data.wellnessOzoneReason &&
      !WELLNESS_OZONE_REASON_OPTIONS.some(o => o.value === data.wellnessOzoneReason)
    ) {
      setFormData(prev => ({
        ...prev,
        wellnessOzoneReason: "other",
        wellnessOzoneReasonOther: data.wellnessOzoneReason || "",
      }));
    }
  }, [isWellness, data.wellnessOzoneReason]);

  useEffect(() => {
    if (!isWellness) return;
    if (
      data.wellnessFunctionalDoctorProgram &&
      !WELLNESS_FUNCTIONAL_DOCTOR_PROGRAM_OPTIONS.some(o => o.value === data.wellnessFunctionalDoctorProgram)
    ) {
      setFormData(prev => ({
        ...prev,
        wellnessFunctionalDoctorProgram: "other",
        wellnessFunctionalDoctorProgramOther: data.wellnessFunctionalDoctorProgram || "",
      }));
    }
  }, [isWellness, data.wellnessFunctionalDoctorProgram]);

  // Normalize reflexology symptom free-text "other"
  useEffect(() => {
    if (!isWellness) return;

    const selected = Array.isArray(data.wellnessReflexologyMainSymptoms)
      ? data.wellnessReflexologyMainSymptoms
      : [];

    if (selected.length === 0) return;

    const optionValues = WELLNESS_REFLEXOLOGY_SYMPTOMS_OPTIONS.map(o => o.value);
    const unknown = selected.filter(v => v && !optionValues.includes(v));

    if (unknown.length > 0) {
      setFormData(prev => ({
        ...prev,
        wellnessReflexologyMainSymptoms: Array.from(
          new Set([
            ...selected.filter(v => optionValues.includes(v) && v !== "other"),
            "other",
          ]),
        ),
        wellnessReflexologyMainSymptomsOther: String(unknown[0]),
      }));
    }
  }, [isWellness, data.wellnessReflexologyMainSymptoms]);

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSymptomTypeChange = (value: string, checked: boolean) => {
    let current = [...formData.vascularSymptomTypes];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("vascularSymptomTypes", current);
  };

  const handleVascularPrimaryGoalToggle = (value: string, checked: boolean) => {
    let current = [...formData.vascularPrimaryGoal];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("vascularPrimaryGoal", current);
    if (value === "other" && !checked) {
      handleInputChange("vascularPrimaryGoalOther", "");
    }
  };

  const handleFertilityCareFocusToggle = (value: string, checked: boolean) => {
    let current = [...formData.fertilityCareFocus];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("fertilityCareFocus", current);
    if (value === "other" && !checked) {
      handleInputChange("fertilityCareFocusOther", "");
    }
  };

  const handleOpticalPrimaryReasonToggle = (value: string, checked: boolean) => {
    let current = [...formData.opticalPrimaryReason];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("opticalPrimaryReason", current);
    if (value === "other" && !checked) {
      handleInputChange("opticalPrimaryReasonOther", "");
    }
  };

  const handleStemCellReasonToggle = (value: string, checked: boolean) => {
    let current = [...formData.stemCellReason];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("stemCellReason", current);
    if (value === "other" && !checked) {
      handleInputChange("stemCellReasonOther", "");
    }
  };

  const handleBariatricPrimaryGoalToggle = (value: string, checked: boolean) => {
    let current = [...formData.bariatricPrimaryGoal];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("bariatricPrimaryGoal", current);
    if (value === "other" && !checked) {
      handleInputChange("bariatricPrimaryGoalOther", "");
    }
  };

  const handleWellnessPrimaryGoalToggle = (value: string, checked: boolean) => {
    let current = [...formData.wellnessPrimaryGoal];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("wellnessPrimaryGoal", current);
    if (value === "other" && !checked) {
      handleInputChange("wellnessPrimaryGoalOther", "");
    }
  };

  const handleDiagnosticsPurposeToggle = (value: string, checked: boolean) => {
    let current = [...formData.diagnosticsPurpose];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("diagnosticsPurpose", current);
    if (value === "other" && !checked) {
      handleInputChange("diagnosticsPurposeOther", "");
    }
  };

  const handleTreatmentAreaChange = (value: string, checked: boolean) => {
    setFormData((prev) => {
      let current = [...prev.hairTransplantTreatmentAreas];
      if (checked) {
        if (!current.includes(value)) current.push(value);
      } else {
        current = current.filter((v) => v !== value);
      }
      return {
        ...prev,
        hairTransplantTreatmentAreas: current,
        ...(value === "other" && !checked
          ? { hairTransplantTreatmentAreasOther: "" }
          : {}),
      };
    });
    setErrors((prev) => {
      const next = { ...prev };
      if (next.hairTransplantTreatmentAreas) next.hairTransplantTreatmentAreas = "";
      if (next.hairTransplantTreatmentAreasOther)
        next.hairTransplantTreatmentAreasOther = "";
      return next;
    });
  };

  const handleHairRelevantFactorsChange = (value: string, checked: boolean) => {
    if (value === "no_factors") {
      if (checked) {
        handleInputChange("hairTransplantRelevantFactors", ["no_factors"]);
      } else {
        handleInputChange("hairTransplantRelevantFactors", []);
      }
      return;
    }
    let current = [...formData.hairTransplantRelevantFactors];
    if (checked && current.includes("no_factors")) {
      current = current.filter((v) => v !== "no_factors");
    }
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("hairTransplantRelevantFactors", current);
  };

  const handlePreviousTreatmentChange = (value: string, checked: boolean) => {
    // If 'none' is selected, clear others
    if (value === 'none') {
        if (checked) {
             handleInputChange("hairTransplantPreviousTreatments", ['none']);
        } else {
             handleInputChange("hairTransplantPreviousTreatments", []);
        }
        return;
    }

    let current = [...formData.hairTransplantPreviousTreatments];
    // If selecting something else, remove 'none'
    if (checked && current.includes('none')) {
        current = current.filter(v => v !== 'none');
    }

    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("hairTransplantPreviousTreatments", current);
  };

  const handleOpticalSymptomChange = (value: string, checked: boolean) => {
    // If 'none' is selected, clear others
    if (value === 'none') {
        if (checked) {
             handleInputChange("opticalSymptoms", ['none']);
        } else {
             handleInputChange("opticalSymptoms", []);
        }
        return;
    }

    let current = [...formData.opticalSymptoms];
    // If selecting something else, remove 'none'
    if (checked && current.includes('none')) {
        current = current.filter(v => v !== 'none');
    }

    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("opticalSymptoms", current);
  };

  const handleStemCellPriorTreatmentChange = (value: string, checked: boolean) => {
    // If 'none' is selected, clear others
    if (value === 'none') {
        if (checked) {
             handleInputChange("stemCellPriorTreatments", ['none']);
        } else {
             handleInputChange("stemCellPriorTreatments", []);
        }
        return;
    }

    let current = [...formData.stemCellPriorTreatments];
    // If selecting something else, remove 'none'
    if (checked && current.includes('none')) {
        current = current.filter(v => v !== 'none');
    }

    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("stemCellPriorTreatments", current);
  };

  const handleDiagnosticSymptomChange = (value: string, checked: boolean) => {
    let current = [...formData.diagnosticsSymptoms];
    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("diagnosticsSymptoms", current);
  };

  const handleDiagnosticConditionChange = (value: string, checked: boolean) => {
    // If 'none' is selected, clear others
    if (value === 'none') {
        if (checked) {
             handleInputChange("diagnosticsConditions", ['none']);
        } else {
             handleInputChange("diagnosticsConditions", []);
        }
        return;
    }

    let current = [...formData.diagnosticsConditions];
    // If selecting something else, remove 'none'
    if (checked && current.includes('none')) {
        current = current.filter(v => v !== 'none');
    }

    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter((v) => v !== value);
    }
    handleInputChange("diagnosticsConditions", current);
  };

  const handleWellnessReflexologySymptomChange = (value: string, checked: boolean) => {
    let current = [...formData.wellnessReflexologyMainSymptoms];

    if (value === "other") {
      if (checked) {
        if (!current.includes("other")) current.push("other");
      } else {
        current = current.filter(v => v !== "other");
        handleInputChange("wellnessReflexologyMainSymptomsOther", "");
      }
      handleInputChange("wellnessReflexologyMainSymptoms", current);
      return;
    }

    // If selecting a specific symptom while "other" is chosen, remove the "other" checkbox.
    if (checked && current.includes("other")) {
      current = current.filter(v => v !== "other");
      handleInputChange("wellnessReflexologyMainSymptomsOther", "");
    }

    if (checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      current = current.filter(v => v !== value);
    }

    handleInputChange("wellnessReflexologyMainSymptoms", current);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (isWellness && !isWellnessHealthcareSlug) {
      if (formData.wellnessPrimaryGoal.length === 0) {
        newErrors.wellnessPrimaryGoal = i18n._(msg`Please select a primary goal`);
      }
      if (
        formData.wellnessPrimaryGoal.includes("other") &&
        !formData.wellnessPrimaryGoalOther.trim()
      ) {
        newErrors.wellnessPrimaryGoalOther = i18n._(msg`Please specify`);
      }

      if (!formData.wellnessTimeline) {
        newErrors.wellnessTimeline = i18n._(msg`Please select a timeline`);
      }
      if (
        formData.wellnessTimeline === "other" &&
        !formData.wellnessTimelineOther.trim()
      ) {
        newErrors.wellnessTimelineOther = i18n._(msg`Please specify`);
      }

      if (!formData.wellnessPreviousExperience) {
        newErrors.wellnessPreviousExperience = i18n._(msg`Please select a previous experience`);
      }

      if (isAcupuncture) {
        if (!formData.wellnessAcupunctureConcern) {
          newErrors.wellnessAcupunctureConcern = i18n._(msg`Please select a primary goal`);
        }
        if (
          formData.wellnessAcupunctureConcern === "other" &&
          !formData.wellnessAcupunctureConcernOther.trim()
        ) {
          newErrors.wellnessAcupunctureConcernOther = i18n._(msg`Please specify`);
        }
        if (!formData.wellnessAcupunctureTargetArea) {
          newErrors.wellnessAcupunctureTargetArea = i18n._(msg`Please select a target area`);
        }
        if (!formData.wellnessAcupunctureFrequency) {
          newErrors.wellnessAcupunctureFrequency = i18n._(msg`Please select a session frequency`);
        }
      }

      if (isHyperbaric) {
        if (!formData.wellnessHyperbaricReason) {
          newErrors.wellnessHyperbaricReason = i18n._(msg`Please select a reason`);
        }
        if (
          formData.wellnessHyperbaricReason === "other" &&
          !formData.wellnessHyperbaricReasonOther.trim()
        ) {
          newErrors.wellnessHyperbaricReasonOther = i18n._(msg`Please specify`);
        }
        if (!formData.wellnessHyperbaricUrgency) {
          newErrors.wellnessHyperbaricUrgency = i18n._(msg`Please select urgency`);
        }
        if (!formData.wellnessHyperbaricSessions) {
          newErrors.wellnessHyperbaricSessions = i18n._(msg`Please select expected sessions`);
        }
      }

      if (isMassage) {
        if (!formData.wellnessMassageFocus) {
          newErrors.wellnessMassageFocus = i18n._(msg`Please select a focus`);
        }
        if (
          formData.wellnessMassageFocus === "other" &&
          !formData.wellnessMassageFocusOther.trim()
        ) {
          newErrors.wellnessMassageFocusOther = i18n._(msg`Please specify`);
        }
        if (!formData.wellnessMassagePressure) {
          newErrors.wellnessMassagePressure = i18n._(msg`Please select a pressure`);
        }
      }

      if (isReflexology) {
        if (!formData.wellnessReflexologyTarget) {
          newErrors.wellnessReflexologyTarget = i18n._(msg`Please select a target`);
        }
        if (!formData.wellnessReflexologyMainSymptoms || formData.wellnessReflexologyMainSymptoms.length === 0) {
          newErrors.wellnessReflexologyMainSymptoms = i18n._(msg`Please select at least one symptom`);
        }
        if (
          formData.wellnessReflexologyMainSymptoms.includes("other") &&
          !formData.wellnessReflexologyMainSymptomsOther.trim()
        ) {
          newErrors.wellnessReflexologyMainSymptomsOther = i18n._(msg`Please specify`);
        }
      }

      if (isOzone) {
        if (!formData.wellnessOzoneReason) {
          newErrors.wellnessOzoneReason = i18n._(msg`Please select a reason`);
        }
        if (
          formData.wellnessOzoneReason === "other" &&
          !formData.wellnessOzoneReasonOther.trim()
        ) {
          newErrors.wellnessOzoneReasonOther = i18n._(msg`Please specify`);
        }
        if (!formData.wellnessOzonePreviousExperience) {
          newErrors.wellnessOzonePreviousExperience = i18n._(msg`Please select yes/no/unsure`);
        }
      }

      if (isFunctionalDoctor) {
        if (!formData.wellnessFunctionalDoctorProgram) {
          newErrors.wellnessFunctionalDoctorProgram = i18n._(msg`Please select a program`);
        }
        if (
          formData.wellnessFunctionalDoctorProgram === "other" &&
          !formData.wellnessFunctionalDoctorProgramOther.trim()
        ) {
          newErrors.wellnessFunctionalDoctorProgramOther = i18n._(msg`Please specify`);
        }
        if (!formData.wellnessFunctionalDoctorExperience) {
          newErrors.wellnessFunctionalDoctorExperience = i18n._(msg`Please select yes/no/unsure`);
        }
      }
    }
    
    if (isVascular) {
      if (formData.vascularPrimaryGoal.length === 0) newErrors.vascularPrimaryGoal = i18n._(msg`Please select at least one option`);
      if (formData.vascularPrimaryGoal.includes("other") && !formData.vascularPrimaryGoalOther.trim()) {
         newErrors.vascularPrimaryGoal = i18n._(msg`Please specify`);
      }

      if (!formData.vascularSymptoms) newErrors.vascularSymptoms = i18n._(msg`Please select symptom status`);
      
      if (["yes_daily", "yes_occasionally"].includes(formData.vascularSymptoms)) {
        if (formData.vascularSymptomTypes.length === 0) {
          newErrors.vascularSymptomTypes = i18n._(msg`Please select at least one symptom`);
        }
         if (formData.vascularSymptomTypes.includes("other") && !formData.vascularSymptomOther.trim()) {
             newErrors.vascularSymptomTypes = i18n._(msg`Please specify other symptoms`);
         }
      }

      if (!formData.vascularDiagnosisStatus) newErrors.vascularDiagnosisStatus = i18n._(msg`Please select diagnosis status`);
      if (!formData.vascularUrgency) newErrors.vascularUrgency = i18n._(msg`Please select urgency`);
      if (!formData.vascularProceduralExpectation) newErrors.vascularProceduralExpectation = i18n._(msg`Please select expectation`);
    }

    if (isHairTransplant) {
      if (formData.hairTransplantTreatmentAreas.length === 0) newErrors.hairTransplantTreatmentAreas = i18n._(msg`Please select at least one area`);
      if (
        formData.hairTransplantTreatmentAreas.includes("other") &&
        !formData.hairTransplantTreatmentAreasOther.trim()
      ) {
        newErrors.hairTransplantTreatmentAreasOther = i18n._(
          msg`Please describe the area or treatment you have in mind.`,
        );
      }
      if (!formData.hairTransplantProgression) newErrors.hairTransplantProgression = i18n._(msg`Please select progression status`);
      if (formData.hairTransplantRelevantFactors.length === 0) {
        newErrors.hairTransplantRelevantFactors = i18n._(msg`Please select at least one option`);
      }
      if (formData.hairTransplantPreviousTreatments.length === 0) newErrors.hairTransplantPreviousTreatments = i18n._(msg`Please select at least one option`);
      if (!formData.hairTransplantOutcomeExpectation) newErrors.hairTransplantOutcomeExpectation = i18n._(msg`Please select expected outcome`);
      if (!formData.hairTransplantTimeline) newErrors.hairTransplantTimeline = i18n._(msg`Please select a timeline`);
    }

    if (isOptical) {
      if (formData.opticalPrimaryReason.length === 0) newErrors.opticalPrimaryReason = i18n._(msg`Please select a primary reason`);
      if (formData.opticalPrimaryReason.includes("other") && !formData.opticalPrimaryReasonOther.trim()) {
         newErrors.opticalPrimaryReason = i18n._(msg`Please specify`);
      }
      if (!formData.opticalVisionCorrection) newErrors.opticalVisionCorrection = i18n._(msg`Please select vision correction status`);
      if (!formData.opticalLaterality) newErrors.opticalLaterality = i18n._(msg`Please select affected eye(s)`);
      if (formData.opticalSymptoms.length === 0) newErrors.opticalSymptoms = i18n._(msg`Please select at least one symptom`);
      if (!formData.opticalProcedureInterest) newErrors.opticalProcedureInterest = i18n._(msg`Please select procedure interest`);
    }

    if (isPlasticSurgery) {
      if (!formData.plasticAreaOfInterest) newErrors.plasticAreaOfInterest = i18n._(msg`Please select an area of interest`);
      if (formData.plasticAreaOfInterest === "other" && !formData.plasticAreaOfInterestOther.trim()) {
         newErrors.plasticAreaOfInterest = i18n._(msg`Please specify`);
      }

      if (!formData.plasticProcedureType) newErrors.plasticProcedureType = i18n._(msg`Please select a procedure type`);
      if (formData.plasticProcedureType === "other" && !formData.plasticProcedureTypeOther.trim()) {
         newErrors.plasticProcedureType = i18n._(msg`Please specify`);
      }

      if (!formData.plasticSpecificConcern) newErrors.plasticSpecificConcern = i18n._(msg`Please select a specific concern`);
      if (formData.plasticSpecificConcern === "other" && !formData.plasticSpecificConcernOther.trim()) {
         newErrors.plasticSpecificConcern = i18n._(msg`Please specify`);
      }

      if (!formData.plasticPreviousSurgery) newErrors.plasticPreviousSurgery = i18n._(msg`Please indicate previous surgery`);
      
      if (!formData.plasticOutcomePriority) newErrors.plasticOutcomePriority = i18n._(msg`Please select an outcome priority`);
       if (formData.plasticOutcomePriority === "other" && !formData.plasticOutcomePriorityOther.trim()) {
         newErrors.plasticOutcomePriority = i18n._(msg`Please specify`);
      }
    }

    if (isDiagnostics) {
      if (!formData.diagnosticsCategory) {
          newErrors.diagnosticsCategory = i18n._(msg`Please select a category`);
      } else if (formData.diagnosticsCategory === "imaging") {
          // Validate Imaging
          if (formData.diagnosticsPurpose.length === 0) newErrors.diagnosticsPurpose = i18n._(msg`Please select at least one option`);
          if (formData.diagnosticsPurpose.includes("other") && !formData.diagnosticsPurposeOther.trim()) {
             newErrors.diagnosticsPurpose = i18n._(msg`Please specify`);
          }
          if (!formData.diagnosticsType) newErrors.diagnosticsType = i18n._(msg`Please select a diagnostic type`);
          if (!formData.diagnosticsArea) newErrors.diagnosticsArea = i18n._(msg`Please select an area`);
          if (formData.diagnosticsArea === "other" && !formData.diagnosticsAreaOther.trim()) {
             newErrors.diagnosticsArea = i18n._(msg`Please specify`);
          }
           if (!formData.diagnosticsTimeSensitivity) newErrors.diagnosticsTimeSensitivity = i18n._(msg`Please select time sensitivity`);

      } else if (formData.diagnosticsCategory === "diagnostics") {
          // Validate Lab Diagnostics
          if (!formData.diagnosticsReason) newErrors.diagnosticsReason = i18n._(msg`Please select a reason`);
          if (formData.diagnosticsReason === "other" && !formData.diagnosticsReasonOther.trim()) {
             newErrors.diagnosticsReason = i18n._(msg`Please specify`);
          }

          if (!formData.diagnosticsHasSymptoms) newErrors.diagnosticsHasSymptoms = i18n._(msg`Please answer`);
          if (formData.diagnosticsHasSymptoms === "yes") {
              if (formData.diagnosticsSymptoms.length === 0) newErrors.diagnosticsSymptoms = i18n._(msg`Please select at least one symptom`);
              if (formData.diagnosticsSymptoms.includes("other") && !formData.diagnosticsSymptomOther.trim()) {
                  newErrors.diagnosticsSymptoms = i18n._(msg`Please specify`);
              }
          }

          if (formData.diagnosticsConditions.length === 0) newErrors.diagnosticsConditions = i18n._(msg`Please select at least one (or None)`);
          if (formData.diagnosticsConditions.includes("other") && !formData.diagnosticsConditionOther.trim()) {
               newErrors.diagnosticsConditions = i18n._(msg`Please specify`);
          }

          if (!formData.diagnosticsDoctorStatus) newErrors.diagnosticsDoctorStatus = i18n._(msg`Please select doctor involvement`);
          if (formData.diagnosticsDoctorStatus === "yes_list" && !formData.diagnosticsDoctorList.trim()) {
               newErrors.diagnosticsDoctorList = i18n._(msg`Please enter requested tests`);
          }

          if (!formData.diagnosticsScope) newErrors.diagnosticsScope = i18n._(msg`Please select scope`);
          
          if (!formData.diagnosticsRecentTesting) newErrors.diagnosticsRecentTesting = i18n._(msg`Please answer`);
          if (formData.diagnosticsRecentTesting === "yes") {
               if (!formData.diagnosticsShareRecentResults) newErrors.diagnosticsShareRecentResults = i18n._(msg`Please answer`);
          }

          if (!formData.diagnosticsLabUrgency) newErrors.diagnosticsLabUrgency = i18n._(msg`Please select urgency`);
          if (!formData.diagnosticsFasting) newErrors.diagnosticsFasting = i18n._(msg`Please answer`);
          if (!formData.diagnosticsResultsUsage) newErrors.diagnosticsResultsUsage = i18n._(msg`Please select result usage`);
          if (!formData.diagnosticsFollowUp) newErrors.diagnosticsFollowUp = i18n._(msg`Please answer`);
      }
    }

    if (isFertility) {
      if (formData.fertilityCareFocus.length === 0) newErrors.fertilityCareFocus = i18n._(msg`Please select at least one option`);
      if (formData.fertilityCareFocus.includes("other") && !formData.fertilityCareFocusOther.trim()) {
         newErrors.fertilityCareFocus = i18n._(msg`Please specify`);
      }
      if (!formData.fertilityPatientType) newErrors.fertilityPatientType = i18n._(msg`Please select a patient type`);
      if (!formData.fertilityPreviousAttempts) newErrors.fertilityPreviousAttempts = i18n._(msg`Please indicate previous attempts`);
      if (!formData.fertilityKnownDiagnosis) newErrors.fertilityKnownDiagnosis = i18n._(msg`Please indicate known diagnosis`);
      if (!formData.fertilityTimeline) newErrors.fertilityTimeline = i18n._(msg`Please select a timeline`);
    }

    if (isStemCell) {
      if (formData.stemCellReason.length === 0) newErrors.stemCellReason = i18n._(msg`Please select a reason`);
      if (formData.stemCellReason.includes("other") && !formData.stemCellReasonOther.trim()) {
         newErrors.stemCellReason = i18n._(msg`Please specify`);
      }
      if (!formData.stemCellLocation) newErrors.stemCellLocation = i18n._(msg`Please select a location`);
      if (!formData.stemCellDiagnosisStatus) newErrors.stemCellDiagnosisStatus = i18n._(msg`Please select diagnosis status`);
      if (!formData.stemCellTreatmentGoal) newErrors.stemCellTreatmentGoal = i18n._(msg`Please select a treatment goal`);
      if (formData.stemCellPriorTreatments.length === 0) newErrors.stemCellPriorTreatments = i18n._(msg`Please select at least one option`);
    }

    if (isBariatric) {
      if (formData.bariatricPrimaryGoal.length === 0) newErrors.bariatricPrimaryGoal = i18n._(msg`Please select a primary goal`);
      if (formData.bariatricPrimaryGoal.includes("other") && !formData.bariatricPrimaryGoalOther.trim()) {
         newErrors.bariatricPrimaryGoal = i18n._(msg`Please specify`);
      }
      if (formData.bariatricPriorWeightLoss.length === 0) newErrors.bariatricPriorWeightLoss = i18n._(msg`Please select at least one option`);
      if (!formData.bariatricProcedureInterest) newErrors.bariatricProcedureInterest = i18n._(msg`Please select a procedure`);
      if (!formData.bariatricCurrentDiet) newErrors.bariatricCurrentDiet = i18n._(msg`Please describe your current diet`);
      if (formData.bariatricCurrentDiet === "other" && !formData.bariatricCurrentDietOther.trim()) {
         newErrors.bariatricCurrentDiet = i18n._(msg`Please specify`);
      }
    }

    if (isWellnessHealthcareSlug) {
      if (formData.wellnessPrimaryGoal.length === 0) newErrors.wellnessPrimaryGoal = i18n._(msg`Please select a primary goal`);
      if (formData.wellnessPrimaryGoal.includes("other") && !formData.wellnessPrimaryGoalOther.trim()) {
         newErrors.wellnessPrimaryGoalOther = i18n._(msg`Please specify`);
      }
      if (!formData.wellnessSpecificConcerns) newErrors.wellnessSpecificConcerns = i18n._(msg`Please select a specific concern`);
      if (formData.wellnessSpecificConcerns === "other" && !formData.wellnessSpecificConcernsOther.trim()) {
         newErrors.wellnessSpecificConcerns = i18n._(msg`Please specify`);
      }
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
      return;
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (
      !isVascular &&
      !isHairTransplant &&
      !isOptical &&
      !isPlasticSurgery &&
      !isDiagnostics &&
      !isFertility &&
      !isStemCell &&
      !isBariatric &&
      !isWellness
    ) {
        onNext();
        return;
    }

    if (validate()) {
      const updates: Partial<IntakeData> = {};
      
      if (isVascular) {
        updates.vascularPrimaryGoal = formData.vascularPrimaryGoal.includes("other")
          ? [
              ...formData.vascularPrimaryGoal.filter((v) => v !== "other"),
              ...(formData.vascularPrimaryGoalOther.trim()
                ? [formData.vascularPrimaryGoalOther.trim()]
                : []),
            ]
          : formData.vascularPrimaryGoal;
        updates.vascularSymptoms = formData.vascularSymptoms;
        updates.vascularSymptomTypes = formData.vascularSymptomTypes.includes("other") 
            ? [...formData.vascularSymptomTypes.filter(v => v !== "other"), formData.vascularSymptomOther] 
            : formData.vascularSymptomTypes;
        updates.vascularDiagnosisStatus = formData.vascularDiagnosisStatus;
        updates.vascularUrgency = formData.vascularUrgency;
        updates.vascularProceduralExpectation = formData.vascularProceduralExpectation;
      }

      if (isHairTransplant) {
        updates.hairTransplantRelevantFactors = formData.hairTransplantRelevantFactors;
        updates.hairTransplantTreatmentAreas = formData.hairTransplantTreatmentAreas;
        updates.hairTransplantTreatmentAreasOther = formData.hairTransplantTreatmentAreas.includes(
          "other",
        )
          ? formData.hairTransplantTreatmentAreasOther.trim()
          : "";
        updates.hairTransplantProgression = formData.hairTransplantProgression;
        updates.hairTransplantPreviousTreatments = formData.hairTransplantPreviousTreatments;
        updates.hairTransplantOutcomeExpectation = formData.hairTransplantOutcomeExpectation;
        updates.hairTransplantTimeline = formData.hairTransplantTimeline;
      }

      if (isOptical) {
        updates.opticalPrimaryReason = multiSelectWithOtherToPayload(
          formData.opticalPrimaryReason,
          formData.opticalPrimaryReasonOther,
        );
        updates.opticalVisionCorrection = formData.opticalVisionCorrection;
        updates.opticalLaterality = formData.opticalLaterality;
        updates.opticalSymptoms = formData.opticalSymptoms;
        updates.opticalProcedureInterest = formData.opticalProcedureInterest;
      }

      if (isPlasticSurgery) {
        updates.plasticAreaOfInterest = formData.plasticAreaOfInterest === "other" ? formData.plasticAreaOfInterestOther : formData.plasticAreaOfInterest;
        updates.plasticProcedureType = formData.plasticProcedureType === "other" ? formData.plasticProcedureTypeOther : formData.plasticProcedureType;
        updates.plasticSpecificConcern = formData.plasticSpecificConcern === "other" ? formData.plasticSpecificConcernOther : formData.plasticSpecificConcern;
        updates.plasticPreviousSurgery = formData.plasticPreviousSurgery;
        updates.plasticOutcomePriority = formData.plasticOutcomePriority === "other" ? formData.plasticOutcomePriorityOther : formData.plasticOutcomePriority;
      }

      if (isDiagnostics) {
        updates.diagnosticsCategory = formData.diagnosticsCategory as any;
        if (formData.diagnosticsCategory === "imaging") {
            updates.diagnosticsPurpose = formData.diagnosticsPurpose.includes("other")
              ? [
                  ...formData.diagnosticsPurpose.filter((v) => v !== "other"),
                  ...(formData.diagnosticsPurposeOther.trim()
                    ? [formData.diagnosticsPurposeOther.trim()]
                    : []),
                ]
              : formData.diagnosticsPurpose;
            updates.diagnosticsType = formData.diagnosticsType;
            updates.diagnosticsArea = formData.diagnosticsArea === "other" ? formData.diagnosticsAreaOther : formData.diagnosticsArea;
            updates.diagnosticsTimeSensitivity = formData.diagnosticsTimeSensitivity;
        } else if (formData.diagnosticsCategory === "diagnostics") {
            updates.diagnosticsReason = formData.diagnosticsReason === "other" ? formData.diagnosticsReasonOther : formData.diagnosticsReason;
            updates.diagnosticsHasSymptoms = formData.diagnosticsHasSymptoms;
            updates.diagnosticsSymptoms = formData.diagnosticsSymptoms.includes("other") 
              ? [...formData.diagnosticsSymptoms.filter(v => v !== "other"), formData.diagnosticsSymptomOther] 
              : formData.diagnosticsSymptoms;
            updates.diagnosticsConditions = formData.diagnosticsConditions.includes("other") 
              ? [...formData.diagnosticsConditions.filter(v => v !== "other"), formData.diagnosticsConditionOther] 
              : formData.diagnosticsConditions;
            updates.diagnosticsDoctorStatus = formData.diagnosticsDoctorStatus;
            updates.diagnosticsDoctorList = formData.diagnosticsDoctorList;
            updates.diagnosticsScope = formData.diagnosticsScope;
            updates.diagnosticsRecentTesting = formData.diagnosticsRecentTesting;
            updates.diagnosticsShareRecentResults = formData.diagnosticsShareRecentResults;
            updates.diagnosticsLabUrgency = formData.diagnosticsLabUrgency;
            updates.diagnosticsFasting = formData.diagnosticsFasting;
            updates.diagnosticsResultsUsage = formData.diagnosticsResultsUsage;
            updates.diagnosticsFollowUp = formData.diagnosticsFollowUp;
        }
      }

      if (isFertility) {
        updates.fertilityCareFocus = formData.fertilityCareFocus.includes("other")
          ? [
              ...formData.fertilityCareFocus.filter((v) => v !== "other"),
              ...(formData.fertilityCareFocusOther.trim()
                ? [formData.fertilityCareFocusOther.trim()]
                : []),
            ]
          : formData.fertilityCareFocus;
        updates.fertilityPatientType = formData.fertilityPatientType;
        updates.fertilityPreviousAttempts = formData.fertilityPreviousAttempts;
        updates.fertilityKnownDiagnosis = formData.fertilityKnownDiagnosis;
        updates.fertilityTimeline = formData.fertilityTimeline;
      }

      if (isWellness && !isWellnessHealthcareSlug) {
        updates.wellnessPrimaryGoal = multiSelectWithOtherToPayload(
          formData.wellnessPrimaryGoal,
          formData.wellnessPrimaryGoalOther,
        );

        updates.wellnessTimeline =
          formData.wellnessTimeline === "other"
            ? formData.wellnessTimelineOther
            : formData.wellnessTimeline;

        updates.wellnessPreviousExperience = formData.wellnessPreviousExperience;

        if (formData.wellnessAdditionalNotes?.trim()) {
          updates.wellnessAdditionalNotes = formData.wellnessAdditionalNotes.trim();
        }
        
        if (isAcupuncture) {
          updates.wellnessAcupunctureConcern =
            formData.wellnessAcupunctureConcern === "other"
              ? formData.wellnessAcupunctureConcernOther
              : formData.wellnessAcupunctureConcern;
          updates.wellnessAcupunctureTargetArea = formData.wellnessAcupunctureTargetArea;
          updates.wellnessAcupunctureFrequency = formData.wellnessAcupunctureFrequency;
        }
        
        if (isHyperbaric) {
          updates.wellnessHyperbaricReason =
            formData.wellnessHyperbaricReason === "other"
              ? formData.wellnessHyperbaricReasonOther
              : formData.wellnessHyperbaricReason;
          updates.wellnessHyperbaricUrgency = formData.wellnessHyperbaricUrgency;
          updates.wellnessHyperbaricSessions = formData.wellnessHyperbaricSessions;
        }
        
        if (isMassage) {
          updates.wellnessMassageFocus =
            formData.wellnessMassageFocus === "other"
              ? formData.wellnessMassageFocusOther
              : formData.wellnessMassageFocus;
          updates.wellnessMassagePressure = formData.wellnessMassagePressure;
        }
        
        if (isReflexology) {
          updates.wellnessReflexologyTarget = formData.wellnessReflexologyTarget;
          updates.wellnessReflexologyMainSymptoms = formData.wellnessReflexologyMainSymptoms.includes("other")
            ? [
                ...formData.wellnessReflexologyMainSymptoms.filter(v => v !== "other"),
                formData.wellnessReflexologyMainSymptomsOther,
              ].filter(Boolean)
            : formData.wellnessReflexologyMainSymptoms;
        }
        
        if (isOzone) {
          updates.wellnessOzoneReason =
            formData.wellnessOzoneReason === "other"
              ? formData.wellnessOzoneReasonOther
              : formData.wellnessOzoneReason;
          updates.wellnessOzonePreviousExperience = formData.wellnessOzonePreviousExperience;
        }
        
        if (isFunctionalDoctor) {
          updates.wellnessFunctionalDoctorProgram =
            formData.wellnessFunctionalDoctorProgram === "other"
              ? formData.wellnessFunctionalDoctorProgramOther
              : formData.wellnessFunctionalDoctorProgram;
          updates.wellnessFunctionalDoctorExperience = formData.wellnessFunctionalDoctorExperience;
        }
      }

      if (isStemCell) {
        updates.stemCellReason = multiSelectWithOtherToPayload(
          formData.stemCellReason,
          formData.stemCellReasonOther,
        );
        updates.stemCellLocation = formData.stemCellLocation;
        updates.stemCellDiagnosisStatus = formData.stemCellDiagnosisStatus;
        updates.stemCellTreatmentGoal = formData.stemCellTreatmentGoal;
        updates.stemCellPriorTreatments = formData.stemCellPriorTreatments;
      }

      if (isBariatric) {
        updates.bariatricPrimaryGoal = multiSelectWithOtherToPayload(
          formData.bariatricPrimaryGoal,
          formData.bariatricPrimaryGoalOther,
        );
        updates.bariatricPriorWeightLoss = formData.bariatricPriorWeightLoss;
        updates.bariatricProcedureInterest = formData.bariatricProcedureInterest;
        updates.bariatricCurrentDiet =
          formData.bariatricCurrentDiet === "other"
            ? formData.bariatricCurrentDietOther
            : formData.bariatricCurrentDiet;
      }
      
      if (isWellnessHealthcareSlug) {
        updates.wellnessPrimaryGoal = multiSelectWithOtherToPayload(
          formData.wellnessPrimaryGoal,
          formData.wellnessPrimaryGoalOther,
        );
        updates.wellnessSpecificConcerns = formData.wellnessSpecificConcerns === "other" ? formData.wellnessSpecificConcernsOther : formData.wellnessSpecificConcerns;
        updates.wellnessCurrentPractices = formData.wellnessCurrentPractices;
      }

      onUpdate(updates);
      onNext();
    }
  };


  if (!isVascular && !isHairTransplant && !isOptical && !isPlasticSurgery && !isDiagnostics && !isFertility && !isStemCell && !isBariatric && !isWellness) {

      return (
        <div className="max-w-4xl mx-auto space-y-6 px-4 lg:px-0">
             <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex items-center justify-center text-gray-400">
                <p><Trans>No detailed questions available for this procedure.</Trans></p>
            </div>
            <IntakeNavigation
                 onBack={onBack}
                 onNext={onNext}
                 nextLabel={<Trans>Continue to Document</Trans>}
            />
        </div>
      )
  }

  // Hair Transplant UI
  if (isHairTransplant) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Hair Transplant Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide details about your hair restoration goals to help us plan your treatment.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">

          {/* Treatment Areas */}
          <div className="space-y-4">
              <Label className="text-base font-semibold text-gray-800"><Trans>Select the area(s) you want treated</Trans></Label>
              <p className="text-sm text-gray-600">
                <Trans>
                  The diagram is a general scalp map — skip beard if it does not apply to you.
                </Trans>
              </p>
              <div className="flex justify-center py-4 bg-gray-50/50 rounded-xl border border-gray-100">
                  {/* <ThreeDHead
                      selectedAreas={formData.hairTransplantTreatmentAreas}
                      onChange={handleTreatmentAreaChange}
                  /> */}
                  <HairAreaSelector2D
                      selectedAreas={formData.hairTransplantTreatmentAreas}
                      onChange={handleTreatmentAreaChange}
                      otherText={formData.hairTransplantTreatmentAreasOther}
                      onOtherTextChange={(v) =>
                        handleInputChange("hairTransplantTreatmentAreasOther", v)
                      }
                  />
               </div>
              {errors.hairTransplantTreatmentAreas && <p className="text-red-500 text-sm">{errors.hairTransplantTreatmentAreas}</p>}
              {errors.hairTransplantTreatmentAreasOther && (
                <p className="text-red-500 text-sm">{errors.hairTransplantTreatmentAreasOther}</p>
              )}
          </div>

          {/* Progression */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>How is your hair loss changing over time?</Trans></Label>
              <Select 
                  value={formData.hairTransplantProgression} 
                  onValueChange={(val) => {
                    handleInputChange("hairTransplantProgression", val);
                  }}
              >
                  <SelectTrigger className={errors.hairTransplantProgression ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select progression status`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {HAIR_PROGRESSION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.hairTransplantProgression && <p className="text-red-500 text-sm">{errors.hairTransplantProgression}</p>}
          </div>

          {/* Hormone / life-stage context */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800">
              <Trans>Do any of these apply to you?</Trans>
            </Label>
            <p className="text-sm text-gray-600">
              <Trans>
                Helps us plan safe treatment. Select all that apply, or “None of these apply”.
              </Trans>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {HAIR_RELEVANT_FACTOR_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`hair-factor-${opt.value}`}
                    checked={formData.hairTransplantRelevantFactors.includes(opt.value)}
                    onCheckedChange={(checked) =>
                      handleHairRelevantFactorsChange(opt.value, checked as boolean)
                    }
                  />
                  <Label htmlFor={`hair-factor-${opt.value}`} className="font-normal">
                    {i18n._(opt.label)}
                  </Label>
                </div>
              ))}
            </div>
            {errors.hairTransplantRelevantFactors && (
              <p className="text-red-500 text-sm">{errors.hairTransplantRelevantFactors}</p>
            )}
          </div>

          {/* Previous Treatments */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Have you tried treatments before?</Trans></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {HAIR_PREVIOUS_TREATMENTS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox 
                              id={`treatment-${opt.value}`}
                              checked={formData.hairTransplantPreviousTreatments.includes(opt.value)}
                              onCheckedChange={(checked) => handlePreviousTreatmentChange(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`treatment-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
              {errors.hairTransplantPreviousTreatments && <p className="text-red-500 text-sm">{errors.hairTransplantPreviousTreatments}</p>}
          </div>

          {/* Outcome Expectation */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What result matters most to you?</Trans></Label>
              <Select 
                  value={formData.hairTransplantOutcomeExpectation} 
                  onValueChange={(val) => {
                    handleInputChange("hairTransplantOutcomeExpectation", val);
                  }}
              >
                  <SelectTrigger className={errors.hairTransplantOutcomeExpectation ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select desired outcome`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {HAIR_OUTCOME_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.hairTransplantOutcomeExpectation && <p className="text-red-500 text-sm">{errors.hairTransplantOutcomeExpectation}</p>}
          </div>

          {/* Timeline */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>When are you hoping to do the procedure?</Trans></Label>
              <Select 
                  value={formData.hairTransplantTimeline} 
                  onValueChange={(val) => {
                    handleInputChange("hairTransplantTimeline", val);
                  }}
              >
                  <SelectTrigger className={errors.hairTransplantTimeline ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select timeline`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {HAIR_TIMELINE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.hairTransplantTimeline && <p className="text-red-500 text-sm">{errors.hairTransplantTimeline}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Optical UI
  if (isOptical) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Optical Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please answer a few questions about your vision to help us prepare for your visit.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Primary Reason */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What are you seeking care for?</Trans></Label>
              <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {OPTICAL_REASON_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox
                              id={`optical-reason-${opt.value}`}
                              checked={formData.opticalPrimaryReason.includes(opt.value)}
                              onCheckedChange={(checked) => handleOpticalPrimaryReasonToggle(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`optical-reason-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
               {formData.opticalPrimaryReason.includes("other") && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify reason`)}
                        value={formData.opticalPrimaryReasonOther}
                        onChange={(e) => handleInputChange("opticalPrimaryReasonOther", e.target.value)}
                        className={errors.opticalPrimaryReason ? "border-red-500" : ""}
                    />
                </div>
              )}
              {errors.opticalPrimaryReason && <p className="text-red-500 text-sm">{errors.opticalPrimaryReason}</p>}
          </div>

          {/* Vision Correction */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Do you currently wear:</Trans></Label>
              <Select 
                  value={formData.opticalVisionCorrection} 
                  onValueChange={(val) => handleInputChange("opticalVisionCorrection", val)}
              >
                  <SelectTrigger className={errors.opticalVisionCorrection ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select outcome`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {OPTICAL_CORRECTION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.opticalVisionCorrection && <p className="text-red-500 text-sm">{errors.opticalVisionCorrection}</p>}
          </div>

          {/* Laterality */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Which eye(s) are affected?</Trans></Label>
              <Select 
                  value={formData.opticalLaterality} 
                  onValueChange={(val) => handleInputChange("opticalLaterality", val)}
              >
                  <SelectTrigger className={errors.opticalLaterality ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select affected eye(s)`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {OPTICAL_LATERALITY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.opticalLaterality && <p className="text-red-500 text-sm">{errors.opticalLaterality}</p>}
          </div>

          {/* Symptoms */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Are you experiencing:</Trans></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {OPTICAL_SYMPTOMS_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox 
                              id={`symptom-${opt.value}`}
                              checked={formData.opticalSymptoms.includes(opt.value)}
                              onCheckedChange={(checked) => handleOpticalSymptomChange(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`symptom-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
              {errors.opticalSymptoms && <p className="text-red-500 text-sm">{errors.opticalSymptoms}</p>}
          </div>

          {/* Procedure Interest */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Are you interested in surgical correction?</Trans></Label>
              <Select 
                  value={formData.opticalProcedureInterest} 
                  onValueChange={(val) => handleInputChange("opticalProcedureInterest", val)}
              >
                  <SelectTrigger className={errors.opticalProcedureInterest ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select interest`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {OPTICAL_PROCEDURE_INTEREST_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.opticalProcedureInterest && <p className="text-red-500 text-sm">{errors.opticalProcedureInterest}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Plastic Surgery UI
  if (isPlasticSurgery) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Plastic Surgery Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please specific details about your enhancement goals to help us prepare.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Area of Interest */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Which area are you seeking treatment for?</Trans></Label>
               <Select 
                  value={formData.plasticAreaOfInterest} 
                  onValueChange={(val) => {
                    handleInputChange("plasticAreaOfInterest", val);
                    if (val !== "other") handleInputChange("plasticAreaOfInterestOther", "");
                  }}
              >
                  <SelectTrigger className={errors.plasticAreaOfInterest ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select area`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {PLASTIC_AREA_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {formData.plasticAreaOfInterest === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify area`)}
                        value={formData.plasticAreaOfInterestOther}
                        onChange={(e) => handleInputChange("plasticAreaOfInterestOther", e.target.value)}
                        className={errors.plasticAreaOfInterest ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.plasticAreaOfInterest && <p className="text-red-500 text-sm">{errors.plasticAreaOfInterest}</p>}
          </div>

          {/* Procedure Type */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Are you looking for:</Trans></Label>
               <Select 
                  value={formData.plasticProcedureType} 
                  onValueChange={(val) => {
                    handleInputChange("plasticProcedureType", val);
                    if (val !== "other") handleInputChange("plasticProcedureTypeOther", "");
                  }}
              >
                  <SelectTrigger className={errors.plasticProcedureType ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select type`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {PLASTIC_PROCEDURE_TYPE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {formData.plasticProcedureType === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify type`)}
                        value={formData.plasticProcedureTypeOther}
                        onChange={(e) => handleInputChange("plasticProcedureTypeOther", e.target.value)}
                        className={errors.plasticProcedureType ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.plasticProcedureType && <p className="text-red-500 text-sm">{errors.plasticProcedureType}</p>}
          </div>

          {/* Specific Concern */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What is your main goal?</Trans></Label>
               <Select 
                  value={formData.plasticSpecificConcern} 
                  onValueChange={(val) => {
                    handleInputChange("plasticSpecificConcern", val);
                    if (val !== "other") handleInputChange("plasticSpecificConcernOther", "");
                  }}
              >
                  <SelectTrigger className={errors.plasticSpecificConcern ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select main goal`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {PLASTIC_CONCERN_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {formData.plasticSpecificConcern === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify goal`)}
                        value={formData.plasticSpecificConcernOther}
                        onChange={(e) => handleInputChange("plasticSpecificConcernOther", e.target.value)}
                        className={errors.plasticSpecificConcern ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.plasticSpecificConcern && <p className="text-red-500 text-sm">{errors.plasticSpecificConcern}</p>}
          </div>

          {/* Previous Surgery */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Have you had surgery in this area before?</Trans></Label>
               <Select 
                  value={formData.plasticPreviousSurgery} 
                  onValueChange={(val) => handleInputChange("plasticPreviousSurgery", val)}
              >
                  <SelectTrigger className={errors.plasticPreviousSurgery ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select yes or no`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {PLASTIC_PREVIOUS_SURGERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.plasticPreviousSurgery && <p className="text-red-500 text-sm">{errors.plasticPreviousSurgery}</p>}
          </div>

          {/* Outcome Priority */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What matters most to you?</Trans></Label>
               <Select 
                  value={formData.plasticOutcomePriority} 
                  onValueChange={(val) => {
                    handleInputChange("plasticOutcomePriority", val);
                    if (val !== "other") handleInputChange("plasticOutcomePriorityOther", "");
                  }}
              >
                  <SelectTrigger className={errors.plasticOutcomePriority ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select priority`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {PLASTIC_OUTCOME_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {formData.plasticOutcomePriority === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify priority`)}
                        value={formData.plasticOutcomePriorityOther}
                        onChange={(e) => handleInputChange("plasticOutcomePriorityOther", e.target.value)}
                        className={errors.plasticOutcomePriority ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.plasticOutcomePriority && <p className="text-red-500 text-sm">{errors.plasticOutcomePriority}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Diagnostics UI
  if (isDiagnostics) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Diagnostic and Imaging Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide details about your imaging needs.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Category Selection */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What type of assessment do you need?</Trans></Label>
               <Select 
                  value={formData.diagnosticsCategory} 
                  onValueChange={(val) => {
                      handleInputChange("diagnosticsCategory", val);
                      setErrors({}); // Clear errors when switching category
                  }}
              >
                  <SelectTrigger className={errors.diagnosticsCategory ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select type`)} />
                  </SelectTrigger>
                  <SelectContent>
                      <SelectItem value="diagnostics"><Trans>Diagnostics (Lab Tests, Panels)</Trans></SelectItem>
                      <SelectItem value="imaging"><Trans>Imaging (MRI, CT, X-Ray, etc.)</Trans></SelectItem>
                  </SelectContent>
              </Select>
               {errors.diagnosticsCategory && <p className="text-red-500 text-sm">{errors.diagnosticsCategory}</p>}
          </div>

          {formData.diagnosticsCategory === "imaging" && (
            <div className="space-y-6 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-2">
                 {/* Purpose */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>Why are you seeking imaging?</Trans></Label>
                    <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {DIAGNOSTICS_PURPOSE_OPTIONS.map(opt => (
                            <div key={opt.value} className="flex items-center space-x-2">
                                <Checkbox 
                                    id={`diag-purpose-${opt.value}`}
                                    checked={formData.diagnosticsPurpose.includes(opt.value)}
                                    onCheckedChange={(checked) => handleDiagnosticsPurposeToggle(opt.value, checked as boolean)}
                                />
                                <Label htmlFor={`diag-purpose-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                            </div>
                        ))}
                    </div>
                    {formData.diagnosticsPurpose.includes("other") && (
                        <div className="mt-2">
                            <Input
                                placeholder={i18n._(msg`Please specify purpose`)}
                                value={formData.diagnosticsPurposeOther}
                                onChange={(e) => handleInputChange("diagnosticsPurposeOther", e.target.value)}
                                className={errors.diagnosticsPurpose ? "border-red-500" : ""}
                            />
                        </div>
                    )}
                    {errors.diagnosticsPurpose && <p className="text-red-500 text-sm">{errors.diagnosticsPurpose}</p>}
                </div>

                {/* Type */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>Type of Imaging Needed</Trans></Label>
                    <Select 
                        value={formData.diagnosticsType} 
                        onValueChange={(val) => handleInputChange("diagnosticsType", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsType ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select type`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_TYPE_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.diagnosticsType && <p className="text-red-500 text-sm">{errors.diagnosticsType}</p>}
                </div>

                {/* Area */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>Which area needs imaging?</Trans></Label>
                    <Select 
                        value={formData.diagnosticsArea} 
                        onValueChange={(val) => {
                            handleInputChange("diagnosticsArea", val);
                            if (val !== "other") handleInputChange("diagnosticsAreaOther", "");
                        }}
                    >
                        <SelectTrigger className={errors.diagnosticsArea ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select area`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_AREA_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {formData.diagnosticsArea === "other" && (
                        <div className="mt-2">
                            <Input
                                placeholder={i18n._(msg`Please specify area`)}
                                value={formData.diagnosticsAreaOther}
                                onChange={(e) => handleInputChange("diagnosticsAreaOther", e.target.value)}
                                className={errors.diagnosticsArea ? "border-red-500" : ""}
                            />
                        </div>
                    )}
                    {errors.diagnosticsArea && <p className="text-red-500 text-sm">{errors.diagnosticsArea}</p>}
                </div>

                {/* Time Sensitivity */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>How soon do you need this completed?</Trans></Label>
                    <Select 
                        value={formData.diagnosticsTimeSensitivity} 
                        onValueChange={(val) => handleInputChange("diagnosticsTimeSensitivity", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsTimeSensitivity ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select priority`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_SENSITIVITY_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.diagnosticsTimeSensitivity && <p className="text-red-500 text-sm">{errors.diagnosticsTimeSensitivity}</p>}
                </div>
            </div>
          )}

          {formData.diagnosticsCategory === "diagnostics" && (
             <div className="space-y-6 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-2">
                 {/* 1. Reason */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>1. What is the main reason you want diagnostic testing?</Trans></Label>
                    <Select 
                        value={formData.diagnosticsReason} 
                        onValueChange={(val) => {
                             handleInputChange("diagnosticsReason", val);
                             if (val !== "other") handleInputChange("diagnosticsReasonOther", "");
                        }}
                    >
                        <SelectTrigger className={errors.diagnosticsReason ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select reason`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_LAB_REASON_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                     {formData.diagnosticsReason === "other" && (
                        <div className="mt-2">
                            <Input
                                placeholder={i18n._(msg`Please specify other reason`)}
                                value={formData.diagnosticsReasonOther}
                                onChange={(e) => handleInputChange("diagnosticsReasonOther", e.target.value)}
                                className={errors.diagnosticsReason ? "border-red-500" : ""}
                            />
                        </div>
                    )}
                    {errors.diagnosticsReason && <p className="text-red-500 text-sm">{errors.diagnosticsReason}</p>}
                </div>

                {/* 2. Symptoms */}
                <div className="space-y-4">
                     <Label className="text-base font-semibold text-gray-800"><Trans>2. Are you currently experiencing symptoms?</Trans></Label>
                     <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox id="diag-symptoms-yes" checked={formData.diagnosticsHasSymptoms === 'yes'} onCheckedChange={(checked) => handleInputChange("diagnosticsHasSymptoms", checked ? 'yes' : '')} />
                             <Label htmlFor="diag-symptoms-yes"><Trans>Yes</Trans></Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="diag-symptoms-no" checked={formData.diagnosticsHasSymptoms === 'no'} onCheckedChange={(checked) => handleInputChange("diagnosticsHasSymptoms", checked ? 'no' : '')} />
                             <Label htmlFor="diag-symptoms-no"><Trans>No</Trans></Label>
                        </div>
                     </div>
                     
                     {formData.diagnosticsHasSymptoms === 'yes' && (
                        <div className="pl-4 border-l-2 border-gray-100 space-y-3">
                             <Label className="font-medium text-gray-700"><Trans>Select all that apply:</Trans></Label>
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {DIAGNOSTICS_LAB_SYMPTOMS_OPTIONS.map(opt => (
                                    <div key={opt.value} className="flex items-center space-x-2">
                                        <Checkbox 
                                            id={`diag-sym-${opt.value}`}
                                            checked={formData.diagnosticsSymptoms.includes(opt.value)}
                                            onCheckedChange={(checked) => handleDiagnosticSymptomChange(opt.value, checked as boolean)}
                                        />
                                        <Label htmlFor={`diag-sym-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                                    </div>
                                ))}
                             </div>
                             {formData.diagnosticsSymptoms.includes("other") && (
                                <div className="mt-2">
                                    <Input
                                        placeholder={i18n._(msg`Please specify other symptoms`)}
                                        value={formData.diagnosticsSymptomOther}
                                        onChange={(e) => handleInputChange("diagnosticsSymptomOther", e.target.value)}
                                    />
                                </div>
                             )}
                              {errors.diagnosticsSymptoms && <p className="text-red-500 text-sm">{errors.diagnosticsSymptoms}</p>}
                        </div>
                     )}
                     {errors.diagnosticsHasSymptoms && <p className="text-red-500 text-sm">{errors.diagnosticsHasSymptoms}</p>}
                </div>

                {/* 3. Known Conditions */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>3. Do you have any diagnosed conditions related to this testing?</Trans></Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {DIAGNOSTICS_LAB_CONDITIONS_OPTIONS.map(opt => (
                            <div key={opt.value} className="flex items-center space-x-2">
                                <Checkbox 
                                    id={`diag-cond-${opt.value}`}
                                    checked={formData.diagnosticsConditions.includes(opt.value)}
                                    onCheckedChange={(checked) => handleDiagnosticConditionChange(opt.value, checked as boolean)}
                                />
                                <Label htmlFor={`diag-cond-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                            </div>
                        ))}
                    </div>
                     {formData.diagnosticsConditions.includes("other") && (
                        <div className="mt-2">
                            <Input
                                placeholder={i18n._(msg`Please specify other conditions`)}
                                value={formData.diagnosticsConditionOther}
                                onChange={(e) => handleInputChange("diagnosticsConditionOther", e.target.value)}
                            />
                        </div>
                     )}
                    {errors.diagnosticsConditions && <p className="text-red-500 text-sm">{errors.diagnosticsConditions}</p>}
                </div>

                {/* 4. Doctor Involvement */}
                <div className="space-y-4">
                    <Label className="text-base font-semibold text-gray-800"><Trans>4. Has a doctor already requested or suggested these tests?</Trans></Label>
                     <Select 
                        value={formData.diagnosticsDoctorStatus} 
                        onValueChange={(val) => handleInputChange("diagnosticsDoctorStatus", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsDoctorStatus ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select status`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_LAB_DOCTOR_STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                     {formData.diagnosticsDoctorStatus === "yes_list" && (
                        <div className="mt-2 animate-in fade-in slide-in-from-top-2">
                             <Label className="text-sm font-medium mb-1 block"><Trans>Upload or enter requested tests</Trans></Label>
                             <textarea 
                                className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.diagnosticsDoctorList ? "border-red-500" : ""}`}
                                placeholder={i18n._(msg`Please list the tests here...`)}
                                value={formData.diagnosticsDoctorList}
                                onChange={(e) => handleInputChange("diagnosticsDoctorList", e.target.value)}
                            /> 
                              {errors.diagnosticsDoctorList && <p className="text-red-500 text-sm">{errors.diagnosticsDoctorList}</p>}
                        </div>
                    )}
                    {errors.diagnosticsDoctorStatus && <p className="text-red-500 text-sm">{errors.diagnosticsDoctorStatus}</p>}
                </div>

                {/* 5. Testing Scope */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>5. What level of testing are you looking for?</Trans></Label>
                     <Select 
                        value={formData.diagnosticsScope} 
                        onValueChange={(val) => handleInputChange("diagnosticsScope", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsScope ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select scope`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_LAB_SCOPE_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.diagnosticsScope && <p className="text-red-500 text-sm">{errors.diagnosticsScope}</p>}
                </div>

                {/* 6. Prior Testing */}
                <div className="space-y-4">
                    <Label className="text-base font-semibold text-gray-800"><Trans>6. Have you had similar tests done recently (last 12 months)?</Trans></Label>
                    <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox id="diag-prior-yes" checked={formData.diagnosticsRecentTesting === 'yes'} onCheckedChange={(checked) => handleInputChange("diagnosticsRecentTesting", checked ? 'yes' : '')} />
                             <Label htmlFor="diag-prior-yes"><Trans>Yes</Trans></Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="diag-prior-no" checked={formData.diagnosticsRecentTesting === 'no'} onCheckedChange={(checked) => handleInputChange("diagnosticsRecentTesting", checked ? 'no' : '')} />
                             <Label htmlFor="diag-prior-no"><Trans>No</Trans></Label>
                        </div>
                         <div className="flex items-center space-x-2">
                            <Checkbox id="diag-prior-unsure" checked={formData.diagnosticsRecentTesting === 'unsure'} onCheckedChange={(checked) => handleInputChange("diagnosticsRecentTesting", checked ? 'unsure' : '')} />
                             <Label htmlFor="diag-prior-unsure"><Trans>Not sure</Trans></Label>
                        </div>
                    </div>
                    {formData.diagnosticsRecentTesting === 'yes' && (
                         <div className="pl-4 border-l-2 border-gray-100 space-y-3">
                            <Label className="font-medium text-gray-700"><Trans>Would you like to share previous results?</Trans></Label>
                            <Select 
                                value={formData.diagnosticsShareRecentResults} 
                                onValueChange={(val) => handleInputChange("diagnosticsShareRecentResults", val)}
                            >
                                <SelectTrigger className={errors.diagnosticsShareRecentResults ? "border-red-500" : ""}>
                                    <SelectValue placeholder={i18n._(msg`Select option`)} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="upload"><Trans>Yes, I will upload results later</Trans></SelectItem>
                                    <SelectItem value="no"><Trans>No</Trans></SelectItem>
                                </SelectContent>
                            </Select>
                             {errors.diagnosticsShareRecentResults && <p className="text-red-500 text-sm">{errors.diagnosticsShareRecentResults}</p>}
                        </div>
                    )}
                    {errors.diagnosticsRecentTesting && <p className="text-red-500 text-sm">{errors.diagnosticsRecentTesting}</p>}
                </div>

                {/* 7. Timing */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>7. How soon do you need the tests completed?</Trans></Label>
                     <Select 
                        value={formData.diagnosticsLabUrgency} 
                        onValueChange={(val) => handleInputChange("diagnosticsLabUrgency", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsLabUrgency ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select urgency`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_LAB_URGENCY_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.diagnosticsLabUrgency && <p className="text-red-500 text-sm">{errors.diagnosticsLabUrgency}</p>}
                </div>
                
                {/* 8. Fasting */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>8. Are you able to fast if required for certain tests?</Trans></Label>
                     <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox id="diag-fasting-yes" checked={formData.diagnosticsFasting === 'yes'} onCheckedChange={(checked) => handleInputChange("diagnosticsFasting", checked ? 'yes' : '')} />
                             <Label htmlFor="diag-fasting-yes"><Trans>Yes</Trans></Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="diag-fasting-no" checked={formData.diagnosticsFasting === 'no'} onCheckedChange={(checked) => handleInputChange("diagnosticsFasting", checked ? 'no' : '')} />
                             <Label htmlFor="diag-fasting-no"><Trans>No</Trans></Label>
                        </div>
                         <div className="flex items-center space-x-2">
                            <Checkbox id="diag-fasting-unsure" checked={formData.diagnosticsFasting === 'unsure'} onCheckedChange={(checked) => handleInputChange("diagnosticsFasting", checked ? 'unsure' : '')} />
                             <Label htmlFor="diag-fasting-unsure"><Trans>Not sure</Trans></Label>
                        </div>
                    </div>
                    {errors.diagnosticsFasting && <p className="text-red-500 text-sm">{errors.diagnosticsFasting}</p>}
                </div>

                {/* 9. Results Usage */}
                <div className="space-y-3">
                     <Label className="text-base font-semibold text-gray-800"><Trans>9. How will you use the results?</Trans></Label>
                     <Select 
                        value={formData.diagnosticsResultsUsage} 
                        onValueChange={(val) => handleInputChange("diagnosticsResultsUsage", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsResultsUsage ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select usage`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_LAB_RESULTS_USAGE_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.diagnosticsResultsUsage && <p className="text-red-500 text-sm">{errors.diagnosticsResultsUsage}</p>}
                </div>

                {/* 10. Follow-up Care */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold text-gray-800"><Trans>10. Do you want help reviewing or interpreting results?</Trans></Label>
                     <Select 
                        value={formData.diagnosticsFollowUp} 
                        onValueChange={(val) => handleInputChange("diagnosticsFollowUp", val)}
                    >
                        <SelectTrigger className={errors.diagnosticsFollowUp ? "border-red-500" : ""}>
                            <SelectValue placeholder={i18n._(msg`Select option`)} />
                        </SelectTrigger>
                        <SelectContent>
                            {DIAGNOSTICS_LAB_FOLLOWUP_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.diagnosticsFollowUp && <p className="text-red-500 text-sm">{errors.diagnosticsFollowUp}</p>}
                </div>

             </div>
          )}

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Fertility UI
  if (isFertility) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Fertility Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide details about your fertility goals to help us create a personalized plan.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Care focus — multi-select */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What are you seeking help with?</Trans></Label>
              <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {FERTILITY_CARE_FOCUS_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox 
                              id={`fertility-focus-${opt.value}`}
                              checked={formData.fertilityCareFocus.includes(opt.value)}
                              onCheckedChange={(checked) => handleFertilityCareFocusToggle(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`fertility-focus-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
               {formData.fertilityCareFocus.includes("other") && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify care focus`)}
                        value={formData.fertilityCareFocusOther}
                        onChange={(e) => handleInputChange("fertilityCareFocusOther", e.target.value)}
                        className={errors.fertilityCareFocus ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.fertilityCareFocus && <p className="text-red-500 text-sm">{errors.fertilityCareFocus}</p>}
          </div>

          {/* Patient Type */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>This care is for:</Trans></Label>
               <Select 
                  value={formData.fertilityPatientType} 
                  onValueChange={(val) => handleInputChange("fertilityPatientType", val)}
              >
                  <SelectTrigger className={errors.fertilityPatientType ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select patient type`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {FERTILITY_PATIENT_TYPE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.fertilityPatientType && <p className="text-red-500 text-sm">{errors.fertilityPatientType}</p>}
          </div>

          {/* Previous Attempts */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Have you tried fertility treatments before?</Trans></Label>
               <Select 
                  value={formData.fertilityPreviousAttempts} 
                  onValueChange={(val) => handleInputChange("fertilityPreviousAttempts", val)}
              >
                  <SelectTrigger className={errors.fertilityPreviousAttempts ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select yes or no`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {FERTILITY_PREVIOUS_ATTEMPTS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.fertilityPreviousAttempts && <p className="text-red-500 text-sm">{errors.fertilityPreviousAttempts}</p>}
          </div>

          {/* Known Diagnosis */}
           <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Are there any known fertility issues?</Trans></Label>
               <Select 
                  value={formData.fertilityKnownDiagnosis} 
                  onValueChange={(val) => handleInputChange("fertilityKnownDiagnosis", val)}
              >
                  <SelectTrigger className={errors.fertilityKnownDiagnosis ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select yes, no, or unsure`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {FERTILITY_KNOWN_DIAGNOSIS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.fertilityKnownDiagnosis && <p className="text-red-500 text-sm">{errors.fertilityKnownDiagnosis}</p>}
          </div>

          {/* Timeline */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>When are you hoping to proceed?</Trans></Label>
               <Select 
                  value={formData.fertilityTimeline} 
                  onValueChange={(val) => handleInputChange("fertilityTimeline", val)}
              >
                  <SelectTrigger className={errors.fertilityTimeline ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select timeline`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {FERTILITY_TIMELINE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.fertilityTimeline && <p className="text-red-500 text-sm">{errors.fertilityTimeline}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6">
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full sm:w-auto h-12 sm:h-14 text-base font-semibold border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 px-8 rounded-xl transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            <Trans>Back</Trans>
          </Button>
          <Button
            onClick={handleNext}
            className="w-full sm:w-auto h-12 sm:h-14 px-8 text-base font-bold bg-[#166B93] hover:bg-[#12587A] text-white shadow-lg shadow-[#166B93]/20 rounded-xl transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <Trans>Continue to Document</Trans>
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Wellness & Healthcare (general procedure slug — must run before `isWellness` because slug contains "wellness")
  if (isWellnessHealthcareSlug) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Leaf className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Wellness & Healthcare</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide details about your wellness goals and health practices to help us plan your care.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800"><Trans>What is your primary goal?</Trans></Label>
            <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {WELLNESS_HEALTHCARE_GOAL_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`wellness-healthcare-goal-${opt.value}`}
                    checked={formData.wellnessPrimaryGoal.includes(opt.value)}
                    onCheckedChange={(checked) =>
                      handleWellnessPrimaryGoalToggle(opt.value, checked as boolean)
                    }
                  />
                  <Label htmlFor={`wellness-healthcare-goal-${opt.value}`} className="font-normal">
                    {i18n._(opt.label)}
                  </Label>
                </div>
              ))}
            </div>
            {formData.wellnessPrimaryGoal.includes("other") && (
              <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <Input
                  placeholder={i18n._(msg`Please specify`)}
                  value={formData.wellnessPrimaryGoalOther}
                  onChange={(e) => handleInputChange("wellnessPrimaryGoalOther", e.target.value)}
                  className={errors.wellnessPrimaryGoal ? "border-red-500" : ""}
                />
              </div>
            )}
            {errors.wellnessPrimaryGoal && <p className="text-red-500 text-sm">{errors.wellnessPrimaryGoal}</p>}
          </div>

          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800"><Trans>Specific Concerns</Trans></Label>
            <Select
              value={formData.wellnessSpecificConcerns}
              onValueChange={(val) => {
                handleInputChange("wellnessSpecificConcerns", val);
                if (val !== "other") handleInputChange("wellnessSpecificConcernsOther", "");
              }}
            >
              <SelectTrigger className={errors.wellnessSpecificConcerns ? "border-red-500" : ""}>
                <SelectValue placeholder={i18n._(msg`Select specific concern`)} />
              </SelectTrigger>
              <SelectContent>
                {WELLNESS_HEALTHCARE_CONCERNS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.wellnessSpecificConcerns === "other" && (
              <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <Input
                  placeholder={i18n._(msg`Please specify`)}
                  value={formData.wellnessSpecificConcernsOther}
                  onChange={(e) => handleInputChange("wellnessSpecificConcernsOther", e.target.value)}
                  className={errors.wellnessSpecificConcerns ? "border-red-500" : ""}
                />
              </div>
            )}
            {errors.wellnessSpecificConcerns && <p className="text-red-500 text-sm">{errors.wellnessSpecificConcerns}</p>}
          </div>

          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800"><Trans>Current Wellness Practices</Trans></Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {WELLNESS_HEALTHCARE_PRACTICES_OPTIONS.map(opt => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`wellness-practice-${opt.value}`}
                    checked={formData.wellnessCurrentPractices?.includes(opt.value)}
                    onCheckedChange={(checked) => handleWellnessPracticesChange(opt.value, checked as boolean)}
                  />
                  <Label htmlFor={`wellness-practice-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <IntakeNavigation
          onBack={onBack}
          onNext={handleNext}
          nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Wellness UI (acupuncture, massage, etc. — not general wellness_healthcare slug)
  if (isWellness && !isWellnessHealthcareSlug) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Wellness Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please answer a few questions about your wellness goals so we can prepare your care plan.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          {/* Primary goal */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800">
              <Trans>What is your main wellness goal?</Trans>
            </Label>
            <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {WELLNESS_PRIMARY_GOAL_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`wellness-therapy-goal-${opt.value}`}
                    checked={formData.wellnessPrimaryGoal.includes(opt.value)}
                    onCheckedChange={(checked) =>
                      handleWellnessPrimaryGoalToggle(opt.value, checked as boolean)
                    }
                  />
                  <Label htmlFor={`wellness-therapy-goal-${opt.value}`} className="font-normal">
                    {i18n._(opt.label)}
                  </Label>
                </div>
              ))}
            </div>
            {errors.wellnessPrimaryGoal && <p className="text-red-500 text-sm">{errors.wellnessPrimaryGoal}</p>}
            {errors.wellnessPrimaryGoalOther && (
              <p className="text-red-500 text-sm">{errors.wellnessPrimaryGoalOther}</p>
            )}

            {formData.wellnessPrimaryGoal.includes("other") && (
              <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <Input
                  placeholder={i18n._(msg`Please specify your goal`)}
                  value={formData.wellnessPrimaryGoalOther}
                  onChange={(e) => handleInputChange("wellnessPrimaryGoalOther", e.target.value)}
                  className={errors.wellnessPrimaryGoalOther ? "border-red-500" : ""}
                />
              </div>
            )}
          </div>

          {/* Previous experience */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800">
              <Trans>Have you tried wellness therapies before?</Trans>
            </Label>
            <Select
              value={formData.wellnessPreviousExperience}
              onValueChange={(val) => handleInputChange("wellnessPreviousExperience", val)}
            >
              <SelectTrigger className={errors.wellnessPreviousExperience ? "border-red-500" : ""}>
                <SelectValue placeholder={i18n._(msg`Select yes/no/unsure`)} />
              </SelectTrigger>
              <SelectContent>
                {WELLNESS_PREVIOUS_EXPERIENCE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {i18n._(opt.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.wellnessPreviousExperience && (
              <p className="text-red-500 text-sm">{errors.wellnessPreviousExperience}</p>
            )}
          </div>

          {/* Timeline */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800">
              <Trans>When are you hoping to proceed?</Trans>
            </Label>
            <Select
              value={formData.wellnessTimeline}
              onValueChange={(val) => {
                handleInputChange("wellnessTimeline", val);
                if (val !== "other") handleInputChange("wellnessTimelineOther", "");
              }}
            >
              <SelectTrigger className={errors.wellnessTimeline ? "border-red-500" : ""}>
                <SelectValue placeholder={i18n._(msg`Select a timeline`)} />
              </SelectTrigger>
              <SelectContent>
                {WELLNESS_TIMELINE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {i18n._(opt.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.wellnessTimeline && <p className="text-red-500 text-sm">{errors.wellnessTimeline}</p>}
            {errors.wellnessTimelineOther && <p className="text-red-500 text-sm">{errors.wellnessTimelineOther}</p>}

            {formData.wellnessTimeline === "other" && (
              <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <Input
                  placeholder={i18n._(msg`Please specify your timeline`)}
                  value={formData.wellnessTimelineOther}
                  onChange={(e) => handleInputChange("wellnessTimelineOther", e.target.value)}
                  className={errors.wellnessTimelineOther ? "border-red-500" : ""}
                />
              </div>
            )}
          </div>

          {/* Additional notes */}
          <div className="space-y-3">
            <Label className="text-base font-semibold text-gray-800">
              <Trans>Any additional notes?</Trans>
            </Label>
            <Input
              placeholder={i18n._(msg`Optional`)}
              value={formData.wellnessAdditionalNotes}
              onChange={(e) => handleInputChange("wellnessAdditionalNotes", e.target.value)}
            />
          </div>

          {/* Acupuncture / Colema */}
          {isAcupuncture && (
            <div className="space-y-6 pt-2">
              <div className="text-sm font-semibold text-gray-900">
                <Trans>Acupuncture / Colema Therapy</Trans>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>What is your main concern?</Trans>
                </Label>
                <Select
                  value={formData.wellnessAcupunctureConcern}
                  onValueChange={(val) => {
                    handleInputChange("wellnessAcupunctureConcern", val);
                    if (val !== "other") handleInputChange("wellnessAcupunctureConcernOther", "");
                  }}
                >
                  <SelectTrigger className={errors.wellnessAcupunctureConcern ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a primary goal`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_PRIMARY_GOAL_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessAcupunctureConcern && (
                  <p className="text-red-500 text-sm">{errors.wellnessAcupunctureConcern}</p>
                )}
                {errors.wellnessAcupunctureConcernOther && (
                  <p className="text-red-500 text-sm">{errors.wellnessAcupunctureConcernOther}</p>
                )}

                {formData.wellnessAcupunctureConcern === "other" && (
                  <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.wellnessAcupunctureConcernOther}
                      onChange={(e) =>
                        handleInputChange("wellnessAcupunctureConcernOther", e.target.value)
                      }
                      className={errors.wellnessAcupunctureConcernOther ? "border-red-500" : ""}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>Which area are you focusing on?</Trans>
                </Label>
                <Select
                  value={formData.wellnessAcupunctureTargetArea}
                  onValueChange={(val) => handleInputChange("wellnessAcupunctureTargetArea", val)}
                >
                  <SelectTrigger className={errors.wellnessAcupunctureTargetArea ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a target area`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_TARGET_AREA_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessAcupunctureTargetArea && (
                  <p className="text-red-500 text-sm">{errors.wellnessAcupunctureTargetArea}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>How often do you want sessions?</Trans>
                </Label>
                <Select
                  value={formData.wellnessAcupunctureFrequency}
                  onValueChange={(val) => handleInputChange("wellnessAcupunctureFrequency", val)}
                >
                  <SelectTrigger className={errors.wellnessAcupunctureFrequency ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a session frequency`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_SESSION_FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessAcupunctureFrequency && (
                  <p className="text-red-500 text-sm">{errors.wellnessAcupunctureFrequency}</p>
                )}
              </div>
            </div>
          )}

          {/* Hyperbaric */}
          {isHyperbaric && (
            <div className="space-y-6 pt-2">
              <div className="text-sm font-semibold text-gray-900">
                <Trans>Hyperbaric Session</Trans>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>What is the main reason for hyperbaric sessions?</Trans>
                </Label>
                <Select
                  value={formData.wellnessHyperbaricReason}
                  onValueChange={(val) => {
                    handleInputChange("wellnessHyperbaricReason", val);
                    if (val !== "other") handleInputChange("wellnessHyperbaricReasonOther", "");
                  }}
                >
                  <SelectTrigger className={errors.wellnessHyperbaricReason ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a reason`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_HYPERBARIC_REASON_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessHyperbaricReason && (
                  <p className="text-red-500 text-sm">{errors.wellnessHyperbaricReason}</p>
                )}

                {formData.wellnessHyperbaricReason === "other" && (
                  <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.wellnessHyperbaricReasonOther}
                      onChange={(e) => handleInputChange("wellnessHyperbaricReasonOther", e.target.value)}
                      className={errors.wellnessHyperbaricReasonOther ? "border-red-500" : ""}
                    />
                  </div>
                )}
                {errors.wellnessHyperbaricReasonOther && (
                  <p className="text-red-500 text-sm">{errors.wellnessHyperbaricReasonOther}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>How urgent is it?</Trans>
                </Label>
                <Select
                  value={formData.wellnessHyperbaricUrgency}
                  onValueChange={(val) => handleInputChange("wellnessHyperbaricUrgency", val)}
                >
                  <SelectTrigger className={errors.wellnessHyperbaricUrgency ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select urgency`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_HYPERBARIC_URGENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessHyperbaricUrgency && (
                  <p className="text-red-500 text-sm">{errors.wellnessHyperbaricUrgency}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>How many sessions do you expect?</Trans>
                </Label>
                <Select
                  value={formData.wellnessHyperbaricSessions}
                  onValueChange={(val) => handleInputChange("wellnessHyperbaricSessions", val)}
                >
                  <SelectTrigger className={errors.wellnessHyperbaricSessions ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select expected sessions`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_HYPERBARIC_SESSIONS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessHyperbaricSessions && (
                  <p className="text-red-500 text-sm">{errors.wellnessHyperbaricSessions}</p>
                )}
              </div>
            </div>
          )}

          {/* Massage */}
          {isMassage && (
            <div className="space-y-6 pt-2">
              <div className="text-sm font-semibold text-gray-900">
                <Trans>Massage Therapy</Trans>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>What are you looking to improve?</Trans>
                </Label>
                <Select
                  value={formData.wellnessMassageFocus}
                  onValueChange={(val) => {
                    handleInputChange("wellnessMassageFocus", val);
                    if (val !== "other") handleInputChange("wellnessMassageFocusOther", "");
                  }}
                >
                  <SelectTrigger className={errors.wellnessMassageFocus ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a focus`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_MASSAGE_FOCUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessMassageFocus && (
                  <p className="text-red-500 text-sm">{errors.wellnessMassageFocus}</p>
                )}

                {formData.wellnessMassageFocus === "other" && (
                  <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.wellnessMassageFocusOther}
                      onChange={(e) => handleInputChange("wellnessMassageFocusOther", e.target.value)}
                      className={errors.wellnessMassageFocusOther ? "border-red-500" : ""}
                    />
                  </div>
                )}
                {errors.wellnessMassageFocusOther && (
                  <p className="text-red-500 text-sm">{errors.wellnessMassageFocusOther}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>What pressure do you prefer?</Trans>
                </Label>
                <Select
                  value={formData.wellnessMassagePressure}
                  onValueChange={(val) => handleInputChange("wellnessMassagePressure", val)}
                >
                  <SelectTrigger className={errors.wellnessMassagePressure ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a pressure`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_MASSAGE_PRESSURE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessMassagePressure && (
                  <p className="text-red-500 text-sm">{errors.wellnessMassagePressure}</p>
                )}
              </div>
            </div>
          )}

          {/* Reflexology */}
          {isReflexology && (
            <div className="space-y-6 pt-2">
              <div className="text-sm font-semibold text-gray-900">
                <Trans>Reflexology</Trans>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>Where do you want reflexology focused?</Trans>
                </Label>
                <Select
                  value={formData.wellnessReflexologyTarget}
                  onValueChange={(val) => handleInputChange("wellnessReflexologyTarget", val)}
                >
                  <SelectTrigger className={errors.wellnessReflexologyTarget ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a target`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_REFLEXOLOGY_TARGET_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessReflexologyTarget && (
                  <p className="text-red-500 text-sm">{errors.wellnessReflexologyTarget}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>Which symptoms are you experiencing?</Trans>
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {WELLNESS_REFLEXOLOGY_SYMPTOMS_OPTIONS.map(opt => (
                    <div key={opt.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`wrx-${opt.value}`}
                        checked={formData.wellnessReflexologyMainSymptoms.includes(opt.value)}
                        onCheckedChange={(checked) =>
                          handleWellnessReflexologySymptomChange(opt.value, checked as boolean)
                        }
                      />
                      <Label htmlFor={`wrx-${opt.value}`} className="font-normal">
                        {i18n._(opt.label)}
                      </Label>
                    </div>
                  ))}
                </div>
                {errors.wellnessReflexologyMainSymptoms && (
                  <p className="text-red-500 text-sm">{errors.wellnessReflexologyMainSymptoms}</p>
                )}

                {formData.wellnessReflexologyMainSymptoms.includes("other") && (
                  <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.wellnessReflexologyMainSymptomsOther}
                      onChange={(e) =>
                        handleInputChange("wellnessReflexologyMainSymptomsOther", e.target.value)
                      }
                      className={errors.wellnessReflexologyMainSymptomsOther ? "border-red-500" : ""}
                    />
                  </div>
                )}
                {errors.wellnessReflexologyMainSymptomsOther && (
                  <p className="text-red-500 text-sm">{errors.wellnessReflexologyMainSymptomsOther}</p>
                )}
              </div>
            </div>
          )}

          {/* Ozone */}
          {isOzone && (
            <div className="space-y-6 pt-2">
              <div className="text-sm font-semibold text-gray-900">
                <Trans>Ozone Therapy</Trans>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>What is your goal for ozone therapy?</Trans>
                </Label>
                <Select
                  value={formData.wellnessOzoneReason}
                  onValueChange={(val) => {
                    handleInputChange("wellnessOzoneReason", val);
                    if (val !== "other") handleInputChange("wellnessOzoneReasonOther", "");
                  }}
                >
                  <SelectTrigger className={errors.wellnessOzoneReason ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a reason`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_OZONE_REASON_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessOzoneReason && (
                  <p className="text-red-500 text-sm">{errors.wellnessOzoneReason}</p>
                )}

                {formData.wellnessOzoneReason === "other" && (
                  <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.wellnessOzoneReasonOther}
                      onChange={(e) => handleInputChange("wellnessOzoneReasonOther", e.target.value)}
                      className={errors.wellnessOzoneReasonOther ? "border-red-500" : ""}
                    />
                  </div>
                )}
                {errors.wellnessOzoneReasonOther && (
                  <p className="text-red-500 text-sm">{errors.wellnessOzoneReasonOther}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>Have you had ozone therapy before?</Trans>
                </Label>
                <Select
                  value={formData.wellnessOzonePreviousExperience}
                  onValueChange={(val) => handleInputChange("wellnessOzonePreviousExperience", val)}
                >
                  <SelectTrigger className={errors.wellnessOzonePreviousExperience ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select yes/no/unsure`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_PREVIOUS_EXPERIENCE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessOzonePreviousExperience && (
                  <p className="text-red-500 text-sm">{errors.wellnessOzonePreviousExperience}</p>
                )}
              </div>
            </div>
          )}

          {/* Functional doctor */}
          {isFunctionalDoctor && (
            <div className="space-y-6 pt-2">
              <div className="text-sm font-semibold text-gray-900">
                <Trans>Functional Doctor</Trans>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>Which program do you want to start?</Trans>
                </Label>
                <Select
                  value={formData.wellnessFunctionalDoctorProgram}
                  onValueChange={(val) => {
                    handleInputChange("wellnessFunctionalDoctorProgram", val);
                    if (val !== "other") handleInputChange("wellnessFunctionalDoctorProgramOther", "");
                  }}
                >
                  <SelectTrigger className={errors.wellnessFunctionalDoctorProgram ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select a program`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_FUNCTIONAL_DOCTOR_PROGRAM_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessFunctionalDoctorProgram && (
                  <p className="text-red-500 text-sm">{errors.wellnessFunctionalDoctorProgram}</p>
                )}

                {formData.wellnessFunctionalDoctorProgram === "other" && (
                  <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.wellnessFunctionalDoctorProgramOther}
                      onChange={(e) => handleInputChange("wellnessFunctionalDoctorProgramOther", e.target.value)}
                      className={errors.wellnessFunctionalDoctorProgramOther ? "border-red-500" : ""}
                    />
                  </div>
                )}
                {errors.wellnessFunctionalDoctorProgramOther && (
                  <p className="text-red-500 text-sm">{errors.wellnessFunctionalDoctorProgramOther}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">
                  <Trans>Have you worked with a functional doctor before?</Trans>
                </Label>
                <Select
                  value={formData.wellnessFunctionalDoctorExperience}
                  onValueChange={(val) => handleInputChange("wellnessFunctionalDoctorExperience", val)}
                >
                  <SelectTrigger className={errors.wellnessFunctionalDoctorExperience ? "border-red-500" : ""}>
                    <SelectValue placeholder={i18n._(msg`Select yes/no/unsure`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {WELLNESS_PREVIOUS_EXPERIENCE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {i18n._(opt.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.wellnessFunctionalDoctorExperience && (
                  <p className="text-red-500 text-sm">{errors.wellnessFunctionalDoctorExperience}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
          onBack={onBack}
          onNext={handleNext}
          nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Stem Cell UI
  if (isStemCell) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Stem Cell Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide details about your condition to help us evaluate the best regenerative treatment options.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Reason */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What condition are you seeking treatment for?</Trans></Label>
              <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {STEM_CELL_REASON_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox
                              id={`stem-reason-${opt.value}`}
                              checked={formData.stemCellReason.includes(opt.value)}
                              onCheckedChange={(checked) => handleStemCellReasonToggle(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`stem-reason-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
               {formData.stemCellReason.includes("other") && (
                  <Input 
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.stemCellReasonOther}
                      onChange={(e) => handleInputChange("stemCellReasonOther", e.target.value)}
                      className="mt-2"
                  />
               )}
               {errors.stemCellReason && <p className="text-red-500 text-sm">{errors.stemCellReason}</p>}
          </div>

          {/* Location */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Where is the primary area of concern?</Trans></Label>
               <Select 
                  value={formData.stemCellLocation} 
                  onValueChange={(val) => handleInputChange("stemCellLocation", val)}
              >
                  <SelectTrigger className={errors.stemCellLocation ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select location`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {STEM_CELL_LOCATION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.stemCellLocation && <p className="text-red-500 text-sm">{errors.stemCellLocation}</p>}
          </div>

          {/* Diagnosis Status */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Have you been formally diagnosed?</Trans></Label>
               <Select 
                  value={formData.stemCellDiagnosisStatus} 
                  onValueChange={(val) => handleInputChange("stemCellDiagnosisStatus", val)}
              >
                  <SelectTrigger className={errors.stemCellDiagnosisStatus ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select yes or no`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {STEM_CELL_DIAGNOSIS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.stemCellDiagnosisStatus && <p className="text-red-500 text-sm">{errors.stemCellDiagnosisStatus}</p>}
          </div>

          {/* Treatment Goal */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What are you hoping to achieve?</Trans></Label>
               <Select 
                  value={formData.stemCellTreatmentGoal} 
                  onValueChange={(val) => handleInputChange("stemCellTreatmentGoal", val)}
              >
                  <SelectTrigger className={errors.stemCellTreatmentGoal ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select goal`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {STEM_CELL_GOAL_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.stemCellTreatmentGoal && <p className="text-red-500 text-sm">{errors.stemCellTreatmentGoal}</p>}
          </div>

          {/* Prior Treatments */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Have you tried other treatments?</Trans></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {STEM_CELL_PRIOR_TREATMENT_OPTIONS.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <Checkbox 
                              id={`sc-prior-${option.value}`}
                              checked={formData.stemCellPriorTreatments.includes(option.value)}
                              onCheckedChange={(checked) => handleStemCellPriorTreatmentChange(option.value, checked as boolean)}
                          />
                          <Label 
                              htmlFor={`sc-prior-${option.value}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer w-full"
                          >
                              {i18n._(option.label)}
                          </Label>
                      </div>
                  ))}
              </div>
               {errors.stemCellPriorTreatments && <p className="text-red-500 text-sm">{errors.stemCellPriorTreatments}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Next Step</Trans>}
        />
      </div>
    );
  }

  // Vascular UI
  if (isVascular) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Vascular Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide specific details about your condition to help our specialists prepare for your case.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Primary concerns — multi-select */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What problems are you seeking help with?</Trans></Label>
              <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PRIMARY_GOAL_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox 
                              id={`vascular-goal-${opt.value}`}
                              checked={formData.vascularPrimaryGoal.includes(opt.value)}
                              onCheckedChange={(checked) => handleVascularPrimaryGoalToggle(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`vascular-goal-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
              {formData.vascularPrimaryGoal.includes("other") && (
                  <Input 
                      placeholder={i18n._(msg`Please specify`)}
                      value={formData.vascularPrimaryGoalOther}
                      onChange={(e) => handleInputChange("vascularPrimaryGoalOther", e.target.value)}
                      className="mt-2"
                  />
              )}
              {errors.vascularPrimaryGoal && <p className="text-red-500 text-sm">{errors.vascularPrimaryGoal}</p>}
          </div>

          {/* Symptoms */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Are you currently experiencing symptoms?</Trans></Label>
              <Select 
                  value={formData.vascularSymptoms} 
                  onValueChange={(val) => handleInputChange("vascularSymptoms", val)}
              >
                  <SelectTrigger className={errors.vascularSymptoms ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select symptom status`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {SYMPTOMS_STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
              {errors.vascularSymptoms && <p className="text-red-500 text-sm">{errors.vascularSymptoms}</p>}
          </div>

          {/* Conditional Symptom Types */}
          {["yes_daily", "yes_occasionally"].includes(formData.vascularSymptoms) && (
               <div className="space-y-3 pl-4 border-l-2 border-[#166B93]/20">
                  <Label className="text-base font-semibold text-gray-800"><Trans>What symptoms?</Trans></Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SYMPTOM_TYPES_OPTIONS.map(opt => (
                          <div key={opt.value} className="flex items-center space-x-2">
                              <Checkbox 
                                  id={`symptom-${opt.value}`}
                                  checked={formData.vascularSymptomTypes.includes(opt.value)}
                                  onCheckedChange={(checked) => handleSymptomTypeChange(opt.value, checked as boolean)}
                              />
                              <Label htmlFor={`symptom-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                          </div>
                      ))}
                  </div>
                   {formData.vascularSymptomTypes.includes("other") && (
                      <Input 
                          placeholder={i18n._(msg`Please specify other symptoms`)}
                          value={formData.vascularSymptomOther}
                          onChange={(e) => handleInputChange("vascularSymptomOther", e.target.value)}
                          className="mt-2"
                      />
                  )}
                   {errors.vascularSymptomTypes && <p className="text-red-500 text-sm">{errors.vascularSymptomTypes}</p>}
               </div>
          )}

          {/* Diagnosis Status */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Have you already been diagnosed by a doctor?</Trans></Label>
               <Select 
                  value={formData.vascularDiagnosisStatus} 
                  onValueChange={(val) => handleInputChange("vascularDiagnosisStatus", val)}
              >
                  <SelectTrigger className={errors.vascularDiagnosisStatus ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select diagnosis status`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {DIAGNOSIS_STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.vascularDiagnosisStatus && <p className="text-red-500 text-sm">{errors.vascularDiagnosisStatus}</p>}
          </div>

          {/* Urgency */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>How soon are you hoping to receive care?</Trans></Label>
               <Select 
                  value={formData.vascularUrgency} 
                  onValueChange={(val) => handleInputChange("vascularUrgency", val)}
              >
                  <SelectTrigger className={errors.vascularUrgency ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select urgency`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {URGENCY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.vascularUrgency && <p className="text-red-500 text-sm">{errors.vascularUrgency}</p>}
          </div>

          {/* Procedural Expectation */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Are you looking for:</Trans></Label>
               <Select 
                  value={formData.vascularProceduralExpectation} 
                  onValueChange={(val) => handleInputChange("vascularProceduralExpectation", val)}
              >
                  <SelectTrigger className={errors.vascularProceduralExpectation ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select expectation`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {EXPECTATION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.vascularProceduralExpectation && <p className="text-red-500 text-sm">{errors.vascularProceduralExpectation}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Bariatric UI
  if (isBariatric) {
    return (
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
        {/* Header */}
        <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
            <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
            <Trans>Bariatric Assessment</Trans>
          </h1>
          <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
            <Trans>
              Please provide details about your weight loss goals to help us prepare for your visit.
            </Trans>
          </p>
        </div>

        <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8 space-y-6">
          
          {/* Primary Goal */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What is your primary goal?</Trans></Label>
              <p className="text-sm text-gray-600"><Trans>Select all that apply.</Trans></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {BARIATRIC_GOAL_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox
                              id={`bariatric-goal-${opt.value}`}
                              checked={formData.bariatricPrimaryGoal.includes(opt.value)}
                              onCheckedChange={(checked) => handleBariatricPrimaryGoalToggle(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`bariatric-goal-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
               {formData.bariatricPrimaryGoal.includes("other") && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify`)}
                        value={formData.bariatricPrimaryGoalOther}
                        onChange={(e) => handleInputChange("bariatricPrimaryGoalOther", e.target.value)}
                        className={errors.bariatricPrimaryGoal ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.bariatricPrimaryGoal && <p className="text-red-500 text-sm">{errors.bariatricPrimaryGoal}</p>}
          </div>

          {/* Prior Weight Loss */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>What weight loss methods have you tried?</Trans></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {BARIATRIC_PRIOR_WEIGHT_LOSS_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                          <Checkbox 
                              id={`bariatric-prior-${opt.value}`}
                              checked={formData.bariatricPriorWeightLoss.includes(opt.value)}
                              onCheckedChange={(checked) => handleBariatricPriorWeightLossChange(opt.value, checked as boolean)}
                          />
                          <Label htmlFor={`bariatric-prior-${opt.value}`} className="font-normal">{i18n._(opt.label)}</Label>
                      </div>
                  ))}
              </div>
              {errors.bariatricPriorWeightLoss && <p className="text-red-500 text-sm">{errors.bariatricPriorWeightLoss}</p>}
          </div>

          {/* Procedure Interest */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>Which procedure are you most interested in?</Trans></Label>
               <Select 
                  value={formData.bariatricProcedureInterest} 
                  onValueChange={(val) => handleInputChange("bariatricProcedureInterest", val)}
              >
                  <SelectTrigger className={errors.bariatricProcedureInterest ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select procedure`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {BARIATRIC_PROCEDURE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {errors.bariatricProcedureInterest && <p className="text-red-500 text-sm">{errors.bariatricProcedureInterest}</p>}
          </div>

          {/* Current Diet */}
          <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-800"><Trans>How would you describe your current eating habits?</Trans></Label>
               <Select 
                  value={formData.bariatricCurrentDiet} 
                  onValueChange={(val) => {
                    handleInputChange("bariatricCurrentDiet", val);
                    if (val !== "other") handleInputChange("bariatricCurrentDietOther", "");
                  }}
              >
                  <SelectTrigger className={errors.bariatricCurrentDiet ? "border-red-500" : ""}>
                      <SelectValue placeholder={i18n._(msg`Select habits`)} />
                  </SelectTrigger>
                  <SelectContent>
                      {BARIATRIC_DIET_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{i18n._(opt.label)}</SelectItem>
                      ))}
                  </SelectContent>
              </Select>
               {formData.bariatricCurrentDiet === "other" && (
                <div className="mt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <Input
                        placeholder={i18n._(msg`Please specify`)}
                        value={formData.bariatricCurrentDietOther}
                        onChange={(e) => handleInputChange("bariatricCurrentDietOther", e.target.value)}
                        className={errors.bariatricCurrentDiet ? "border-red-500" : ""}
                    />
                </div>
              )}
               {errors.bariatricCurrentDiet && <p className="text-red-500 text-sm">{errors.bariatricCurrentDiet}</p>}
          </div>

        </div>

        {/* Navigation Actions */}
        <IntakeNavigation
            onBack={onBack}
            onNext={handleNext}
            nextLabel={<Trans>Continue to Document</Trans>}
        />
      </div>
    );
  }

  // Fallback / Default UI for procedures without specific questions
  const procedureLabel = 
    typeof data.procedures === 'string' 
      ? data.procedures 
      : (data.procedures as any)?.label || "your procedure";



  return (
    <div className="max-w-lg mx-auto text-center space-y-8 pt-12 sm:pt-20 px-4">
      <div className="flex justify-center">
        <div className="rounded-full bg-green-50 p-6 sm:p-8 animate-in zoom-in duration-500">
          <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500" />
        </div>
      </div>
      
      <div className="space-y-3 sm:space-y-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 font-nunito">
          <Trans>All Set!</Trans>
        </h2>
        <p className="text-base sm:text-lg text-gray-600 font-jakarta max-w-md mx-auto leading-relaxed">
           <Trans>
            No additional questions are needed for {procedureLabel}. You can proceed to the next step.
           </Trans>
        </p>
      </div>

      <IntakeNavigation
           onBack={onBack}
           onNext={handleNext}
           nextLabel={<Trans>Continue to Photos</Trans>}
      />
    </div>
  );
}
